// The Asking Machine — ContentIQ's guided setup (Edward, 2026-08-25).
// Step-by-step wizard screens (per his review of the chat-styled first cut:
// "not loving the chat feel — prefer step-by-step wizard"), one question per
// screen with a real Back, drag-and-drop uploads, and a TWO-phase build:
// research runs first and its output — company read, personas, the
// auto-built buyer map — is reviewed before the strategy build starts.
// R1 (Edward, 2026-10-15): the old targeting-scope questions (buyer scope /
// funnel stages / page slicing) are gone — the generator reads the whole
// map; and a project can cover the WHOLE program, not just one component.
//
// Replaces Stage 1 for NEW wizard projects; the Advanced workspace keeps its
// form. All answers land in the same fields the workspace edits.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SearchableSelect from '../../components/SearchableSelect';
import WizardChrome from '../../components/WizardChrome';
import { deriveFormat } from '../../utils/ciqFormats';
import CiqPhaseRail from '../../components/CiqPhaseRail';
import { buildStatusGroupMap, splitByStatusGroup } from '../../utils/statusGroups';
import FileDropZone from '../../components/FileDropZone';
import RichCopyEditor from '../../components/RichCopyEditor';
import TagInput from '../../components/TagInput';
import {
  BRAND, FONTS, HUES, INK, NEUTRAL, RADII, SHADOW, STATE,
  buttonDisabled, buttonLink, buttonPrimary, buttonPrimarySmall, buttonSmall, card, displayFont,
} from '../../utils/wizardTheme';
import {
  GenerationProgress, RESEARCH_PROGRESS_MSGS, STRATEGY_PROGRESS_MSGS,
  Stage2Panel, Stage3Panel, Stage4Panel, StrategyJourney,
} from './ContentIQ';
import DeploymentPanel, { ApproveLaunchPanel } from './DeploymentPanel';
import SalesDeckPanel, { deckRailRow, useDeckRailStep } from './SalesDeckPanel';
import CopyImportPanel from './CopyImportPanel';
import { sanitizeCopyHtml, htmlToText, textToHtml } from '../../utils/richCopy';
import { colorRowIssue, colorsPayload, sameHex } from '../../utils/ciqBrand';
import {
  downloadFileBlob, fmtSize, CIQ_ASSET_MAX_BYTES, CIQ_BRAND_MAX_BYTES,
  oversizeReason, oversizeAdvice, uploadErrorText,
} from '../../utils/ciqAssets';
import { fmtDate } from '../../components/ZbSummary';
import { OUTPUT_DEFS } from './ContentIQStart';
import { PHASES, RAIL, buildDocRail } from '../../utils/ciqRail';
import { SHOW_BUILD_TEAM_WORKFLOW } from '../../utils/packageStatus';

const API = process.env.REACT_APP_API_URL;

// The ONE interface (Edward, 2026-08-26: "use the left-hand navigation of the
// wizard as the only interface"): this screen is both the guided setup for a
// new project AND the permanent home of an existing one. The rail is the map —
// Setup topics stay revisitable with answers pre-filled; Build and Deliver
// sections host the same panels the Advanced workspace uses. The old
// top-stepper shell (ContentIQWizard) is retired; /contentiq/wizard/:id and
// /contentiq/project/:id both land here.
// Setup asks only INPUT questions (facts you already have); the output
// decision — how many pages, which buyers, which stages — is asked AFTER
// research, on top of the actual researched map (Edward, 2026-08-26:
// "collect the basic information, do the basic research, then ask me some
// questions about output"). Deliver shows the whole output chain.
const STEP_TOPIC = {
  goal: 'scope', program: 'scope', component: 'scope', product: 'product',
  context: 'context', assets: 'assets', count: 'assets',
  questions: 'questions', brand: 'deploy',
  review: 'review', 'research-run': 'research', 'research-view': 'research',
  'research-review': 'personas', buyermap: 'personas',
  'strategy-run': 'strategy', 'strategy-view': 'strategy',
  copy: 'deploy', script: 'strategy-view', deploy: 'deploy', launch: 'launch', deck: 'deck',
};

// DemandArc's recommended registration questions (Edward, 2026-09-29):
// Content Syndication / 2-Touch carry NO BANT by default — the PM picks
// whichever of these apply, they land in the BANT box as editable text, and
// nothing downstream distinguishes them from client-supplied. (DA's default
// is the full 4, Decision Builder's is Authority + Timing — those belong to
// their own flows at the fast-follow.)
const RECOMMENDED_QUESTIONS = [
  ['authority', 'Authority',
    "Authority — What's your role in purchasing (technology or service) solutions for your organization?\n- Member of the decision committee\n- Influencer\n- Not involved [Disqualify]"],
  ['timing', 'Timing',
    'Timing — When does your organization expect to begin evaluating a solution like this?\n- Within 6 months\n- 7-12 months\n- More than 12 months\n- No current plans [Disqualify]'],
  ['budget', 'Budget',
    'Budget — Would you like to receive information about attractive pricing?\n- Yes\n- Not at this time [Disqualify]'],
  ['need', 'Need',
    'Need — Which challenge is driving your interest? (tailor the options to the client)\n- [Challenge A]\n- [Challenge B]\n- None of these [Disqualify]'],
];

const RECOMMENDED_TEXT = htmlToText(
  RECOMMENDED_QUESTIONS.map(([, , text]) => textToHtml(text)).join('')).trim();

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '13px',
  fontFamily: FONTS.body, color: INK.primary, background: NEUTRAL.pageBg,
  border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small,
};

// Per-file upload progress (staging round 1: dropping 5 files showed only a
// bare "uploading" for minutes). One row per file: bar, state, error.
// A queued or still-sending file can be cancelled (the 2026-10-28
// walkthrough: a large upload had no way out). Once the bytes are sent the
// server is already storing it, so "processing" has no cancel.
function UploadProgressList({ rows, onCancel }) {
  if (!rows.length) return null;
  const stateLabel = (r) =>
    r.state === 'queued' ? 'queued'
      : r.state === 'uploading' ? `uploading ${r.pct}%`
      : r.state === 'processing' ? 'processing…'
      : r.state === 'done' ? '✓ done'
      : r.state === 'cancelled' ? 'cancelled'
      : `✕ ${r.err || 'failed'}`;
  const stateColor = (r) =>
    r.state === 'done' ? STATE.done.text : r.state === 'failed' ? STATE.danger.text : INK.secondary;
  const cancellable = (r) => onCancel && (r.state === 'queued' || r.state === 'uploading');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '8px' }}>
      {rows.map(r => (
        <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px' }}>
          <span style={{ flexBasis: '220px', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: INK.primary }}>{r.name}</span>
          <span style={{ flexGrow: 1, height: '6px', borderRadius: '3px', background: NEUTRAL.chipBg, overflow: 'hidden' }}>
            <span style={{
              display: 'block', height: '100%', width: `${r.state === 'done' ? 100 : r.pct}%`,
              background: r.state === 'failed' ? STATE.danger.main
                : r.state === 'done' ? STATE.done.main : HUES.strategy.main,
              transition: 'width 0.2s',
            }} />
          </span>
          <span style={{ flexBasis: '210px', flexShrink: 0, color: stateColor(r) }}>{stateLabel(r)}</span>
          <span style={{ flexBasis: '58px', flexShrink: 0 }}>
            {cancellable(r) && (
              <button onClick={() => onCancel(r.key)} title={`Stop uploading ${r.name}`}
                style={{ ...buttonLink, fontSize: '11.5px', color: STATE.danger.text }}>
                Cancel
              </button>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

// Minimal rich-copy editor: a contentEditable that PRESERVES pasted
// formatting (Word/Docs bolds and headings — they tell the build team what
// the headings are) plus a small toolbar. Value in/out is an HTML string;
// saving sanitizes through utils/richCopy (and the backend re-sanitizes).
// A selectable chip (radio/checkbox feel) for wizard choices.
function Pick({ label, selected, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        fontFamily: FONTS.body, fontSize: '13px', cursor: disabled ? 'default' : 'pointer',
        padding: '8px 16px', borderRadius: RADII.control,
        background: selected ? BRAND.button : NEUTRAL.pageBg,
        fontWeight: selected ? 700 : 600,
        color: selected ? NEUTRAL.pageBg : INK.body,
        border: selected ? `2px solid ${BRAND.button}` : `1.5px solid ${NEUTRAL.control}`,
        boxShadow: selected ? SHADOW.focus : 'none',
        opacity: disabled ? 0.5 : 1,
      }}>
      {selected ? '✓ ' : ''}{label}
    </button>
  );
}

const FILE_ACCEPT = '.pdf,.docx,.xlsx,.pptx,.txt,.md,.csv,.png,.jpg,.jpeg,.gif';

// Client-level brand intake (migration 0126 — Edward, 2026-10-21: "brand
// inputs at the client level", collected once, reused across that client's
// programs and projects; today colors travel as email prose and restricted
// terms / legal language have no home). Explicit Save, never debounced
// auto-save — the QARules condition-editor race is the reason. Fetches its
// own data, so it stays in this page file per the component-library rule.
// Colors, never-use words, legal line and notes save together with Save
// brand; logos save the moment they're added. Unsaved edits show a bar with
// its own Save, warn on closing the tab, and report up through onDirtyChange
// so leaving the step can ask first (Edward, 2026-10-22: a color change was
// silently lost after a logo swap had saved instantly).
function ClientBrandCard({ clientId, clientName, onDirtyChange, onSaved }) {
  const [loaded, setLoaded] = useState(false);
  const [version, setVersion] = useState(null);
  const [colorRows, setColorRows] = useState([]);   // [{token, hex}]
  const [terms, setTerms] = useState([]);
  const [legal, setLegal] = useState('');
  const [notes, setNotes] = useState('');
  const [logos, setLogos] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyLogo, setBusyLogo] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);
  const [triedSave, setTriedSave] = useState(false);  // a save stopped on an incomplete color
  const [suggestUrl, setSuggestUrl] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [suggestErr, setSuggestErr] = useState('');

  const seed = useCallback((b) => {
    setVersion(b.version);
    setColorRows(Object.entries(b.colors || {}).map(([token, hex]) => ({ token, hex })));
    setTerms(b.restricted_terms || []);
    setLegal(b.legal_language || '');
    setNotes(b.brand_notes || '');
    setLogos(b.logo_files || []);
    setDirty(false);
    setTriedSave(false);
  }, []);

  useEffect(() => {
    if (!clientId) return;
    setLoaded(false); setErr('');
    axios.get(`${API}/contentiq/clients/${clientId}/brand`)
      .then(r => { seed(r.data); setLoaded(true); })
      .catch(() => { setErr('Could not load this client’s brand.'); setLoaded(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  const mark = (setter) => (v) => { setter(v); setDirty(true); setSaved(false); };
  const setRow = (i, field, v) => {
    setColorRows(rows => rows.map((r, j) => (j === i ? { ...r, [field]: v } : r)));
    setDirty(true); setSaved(false);
  };

  // Candidate colors scraped from the client's site (Edward, 2026-10-22) —
  // the backend only ever SUGGESTS; clicking a swatch adds an unsaved row
  // the PM still names and saves like any hand-entered color.
  const suggestColors = async () => {
    if (!suggestUrl.trim()) { setSuggestErr('Enter the client’s website first.'); return; }
    setSuggesting(true); setSuggestErr(''); setSuggestions(null);
    try {
      const r = await axios.post(`${API}/contentiq/clients/${clientId}/brand/suggest-colors`,
        { url: suggestUrl });
      setSuggestions(r.data.suggestions || []);
    } catch (e) {
      setSuggestErr(e.response?.data?.detail || 'Could not read that website.');
    }
    setSuggesting(false);
  };

  // `fresh` focuses the new row's name box — the one thing it still needs.
  const addSuggestion = (hex) => {
    setColorRows(rows => (rows.some(r => sameHex(r.hex, hex))
      ? rows : [...rows, { token: '', hex, fresh: true }]));
    setDirty(true); setSaved(false);
  };

  // A color the server can't key (no name) or can't show (no hex) stops the
  // save and says so on its row — it used to be dropped without a word.
  const rowIssues = colorRows.map(colorRowIssue);
  const blockedMsg = !triedSave ? ''
    : rowIssues.includes('name') ? 'Name what each color is for (e.g. “buttons”), or remove it with ✕.'
    : rowIssues.includes('hex') ? 'Add the hex for each named color, or remove it with ✕.'
    : '';

  const save = async () => {
    if (rowIssues.some(Boolean)) { setTriedSave(true); return; }
    setSaving(true); setErr('');
    const colors = colorsPayload(colorRows);
    try {
      const r = await axios.put(`${API}/contentiq/clients/${clientId}/brand`, {
        colors, restricted_terms: terms, legal_language: legal, brand_notes: notes,
      });
      seed(r.data); setSaved(true);
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  const uploadLogos = async (files) => {
    setBusyLogo(true); setErr('');
    for (const f of files) {
      const tooBig = oversizeReason(f, CIQ_BRAND_MAX_BYTES);
      if (tooBig) { setErr(`Couldn’t upload ${f.name} — ${tooBig}.`); continue; }
      try {
        const fd = new FormData();
        fd.append('file', f);
        const r = await axios.post(`${API}/contentiq/clients/${clientId}/brand/logos`, fd);
        setVersion(r.data.version); setLogos(r.data.logo_files || []);
      } catch (e) {
        setErr(`Couldn’t upload ${f.name} — ${uploadErrorText(e, CIQ_BRAND_MAX_BYTES)}.`);
      }
    }
    setBusyLogo(false);
    if (onSaved) onSaved();
  };

  const removeLogo = async (logo) => {
    if (!window.confirm(`Remove “${logo.original_filename || 'this logo'}” from ${clientName || 'this client'}’s brand? `
      + 'Every project and sent package for this client loses it.')) return;
    try {
      const r = await axios.delete(`${API}/contentiq/clients/${clientId}/brand/logos/${logo.id}`);
      setLogos(r.data.logo_files || []);
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Couldn’t remove that file.');
    }
  };

  const downloadLogo = async (logo) => {
    try {
      await downloadFileBlob(`${API}/contentiq/clients/${clientId}/brand/logos/${logo.id}`,
        logo.original_filename || 'logo');
    } catch { setErr('Download failed.'); }
  };

  if (!clientId) return null;
  const labelStyle = { fontSize: '12px', color: INK.muted, display: 'block' };
  return (
    <div style={{ ...card, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: INK.primary }}>
          {clientName ? `${clientName}’s brand` : 'Client brand'}
        </div>
        <div style={{ fontSize: '11.5px', color: INK.muted }}>
          client-level — saved once, used by every {clientName || 'client'} program and project
          {version ? ` · v${version}` : ''}
        </div>
      </div>
      {dirty && (
        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          padding: '8px 12px', background: STATE.gate.tint, border: `1px solid ${STATE.gate.border}`, borderRadius: RADII.small }}>
          <span style={{ fontSize: '12.5px', fontWeight: 600, color: blockedMsg ? STATE.danger.text : STATE.gate.deep, flex: '1 1 200px' }}>
            {blockedMsg || 'Unsaved brand changes'}
          </span>
          <button onClick={save} disabled={saving} style={buttonPrimarySmall()}>
            {saving ? 'Saving…' : 'Save brand'}
          </button>
        </div>
      )}
      {!loaded ? <div style={{ fontSize: '12.5px', color: INK.muted }}>Loading…</div> : (
        <>
          <div>
            <span style={labelStyle}>Brand colors (hex)</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
              {colorRows.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input value={r.token} onChange={e => setRow(i, 'token', e.target.value)} autoFocus={!!r.fresh}
                    aria-invalid={triedSave && rowIssues[i] === 'name'}
                    placeholder="what it's for (e.g. buttons, highlight text)"
                    style={{ ...inputStyle, flex: 2,
                      ...(triedSave && rowIssues[i] === 'name' ? { borderColor: STATE.danger.main } : {}) }} />
                  <input value={r.hex} onChange={e => setRow(i, 'hex', e.target.value)}
                    aria-invalid={triedSave && rowIssues[i] === 'hex'}
                    placeholder="hex, e.g. 6437D9"
                    style={{ ...inputStyle, flex: 1, fontFamily: 'monospace',
                      ...(triedSave && rowIssues[i] === 'hex' ? { borderColor: STATE.danger.main } : {}) }} />
                  {/^#?[0-9a-fA-F]{3}$|^#?[0-9a-fA-F]{6}$/.test(r.hex.trim()) && (
                    <span style={{ width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
                      border: `1px solid ${NEUTRAL.control}`,
                      background: `#${r.hex.trim().replace('#', '')}` }} />
                  )}
                  <button onClick={() => { setColorRows(rows => rows.filter((_, j) => j !== i)); setDirty(true); setSaved(false); }}
                    style={{ ...buttonSmall, padding: '4px 8px' }} title="Remove color">✕</button>
                </div>
              ))}
              <button onClick={() => { setColorRows(rows => [...rows, { token: '', hex: '', fresh: true }]); setDirty(true); setSaved(false); }}
                style={{ ...buttonSmall, alignSelf: 'flex-start' }}>+ Add color</button>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '2px' }}>
                <input value={suggestUrl} onChange={e => setSuggestUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') suggestColors(); }}
                  placeholder={`client website, e.g. ${(clientName || 'client').toLowerCase().replace(/[^a-z0-9]/g, '')}.com`}
                  style={{ ...inputStyle, flex: 1 }} />
                <button onClick={suggestColors} disabled={suggesting}
                  style={{ ...buttonSmall, whiteSpace: 'nowrap', ...(suggesting ? buttonDisabled : {}) }}>
                  {suggesting ? 'Reading site…' : 'Suggest from website'}
                </button>
              </div>
              {suggestErr && <div style={{ fontSize: '11.5px', color: STATE.danger.text }}>{suggestErr}</div>}
              {suggestions && suggestions.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                  <span style={{ fontSize: '11.5px', color: INK.muted }}>Click to add:</span>
                  {suggestions.map(s => {
                    const added = colorRows.some(r => sameHex(r.hex, s.hex));
                    return (
                      <button key={s.hex} onClick={() => addSuggestion(s.hex)} disabled={added}
                        title={added ? `${s.hex} is already in the colors above` : `Add ${s.hex} · ${s.source}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px',
                          cursor: added ? 'default' : 'pointer', opacity: added ? 0.6 : 1,
                          fontFamily: FONTS.body, fontSize: '11.5px', color: added ? STATE.done.text : INK.body,
                          background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.control}`,
                          borderRadius: RADII.control, padding: '3px 8px' }}>
                        <span aria-hidden="true">{added ? '✓' : '+'}</span>
                        <span style={{ width: '13px', height: '13px', borderRadius: '3px',
                          border: `1px solid ${NEUTRAL.control}`, background: s.hex }} />
                        {s.hex}
                      </button>
                    );
                  })}
                  <span style={{ fontSize: '11px', color: INK.muted }}>— picked from the site; add one, then name what it's for</span>
                </div>
              )}
            </div>
          </div>
          <div>
            <span style={labelStyle}>Never-use words — terms the client never allows</span>
            <TagInput tags={terms} onChange={mark(setTerms)} placeholder="Type a term and press Enter" />
          </div>
          <label style={labelStyle}>
            Required legal language — carried verbatim into every build package
            <textarea value={legal} onChange={e => mark(setLegal)(e.target.value)} rows={3}
              style={{ ...inputStyle, marginTop: '4px', resize: 'vertical' }}
              placeholder="e.g. mandated disclaimers, trademark lines" />
          </label>
          <label style={labelStyle}>
            Brand notes — any other guidance, in the client's own words (the build team sees these)
            <textarea value={notes} onChange={e => mark(setNotes)(e.target.value)} rows={2}
              style={{ ...inputStyle, marginTop: '4px', resize: 'vertical' }}
              placeholder='e.g. "Use purple for the boxes and highlight text"' />
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button onClick={save} disabled={!dirty || saving}
              style={{ ...buttonPrimarySmall(), ...(!dirty || saving ? buttonDisabled : {}) }}>
              {saving ? 'Saving…' : 'Save brand'}
            </button>
            {saved && <span style={{ fontSize: '12px', color: STATE.done.text }}>✓ Saved</span>}
            {blockedMsg && <span role="alert" style={{ fontSize: '12px', color: STATE.danger.text }}>{blockedMsg}</span>}
            {!dirty && !saved && (
              <span style={{ fontSize: '11.5px', color: INK.muted }}>
                {suggestions && suggestions.length > 0
                  ? 'Nothing to save yet — click a suggested color to add it, then name what it’s for.'
                  : 'Colors, never-use words, legal line and notes save together.'}
              </span>
            )}
          </div>
          <div style={{ borderTop: `1px solid ${NEUTRAL.chipBg}`, paddingTop: '10px' }}>
            <span style={labelStyle}>Client logos — reused on every build (png, jpg, svg) · saved as soon as you add or remove one</span>
            {logos.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', margin: '6px 0' }}>
                {logos.map(l => (
                  <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px' }}>
                    <button onClick={() => downloadLogo(l)} title="Download"
                      style={buttonLink}>
                      {l.original_filename || 'logo'}
                    </button>
                    <span style={{ color: INK.muted, fontSize: '11px' }}>
                      {l.size_bytes ? fmtSize(l.size_bytes) : ''}
                    </span>
                    <button onClick={() => removeLogo(l)} style={{ ...buttonSmall, padding: '2px 7px' }} title="Remove">✕</button>
                  </div>
                ))}
              </div>
            )}
            <FileDropZone accept=".png,.jpg,.jpeg,.gif,.svg" disabled={busyLogo} multiple
              label={busyLogo ? 'Uploading…' : 'Drop client logos here — or click to browse'}
              hint="transparent PNG or SVG preferred — 10 MB each"
              onFiles={uploadLogos} />
          </div>
          {err && <div style={{ fontSize: '12.5px', color: STATE.danger.text }}>{err}</div>}
        </>
      )}
    </div>
  );
}

// What the research could actually reach (Edward, 2026-08-26: "I'd like to
// see what of the research was reachable/used vs not"). Used sources come
// from the stored profile's provenance; unreachable ones from each source's
// recorded fetch error.
// What the research read and what it didn't (Edward, 2026-08-26 and
// 2026-10-22: "it's not clear all of my resources were used — my screenshots
// aren't"). Read sources come from the stored profile's provenance; not-read
// ones from its sources_skipped (newer runs) or, for older runs, inferred
// here: an image is never read, anything else simply wasn't in that run.
function researchSourceRows(research, sources) {
  const used = research?.sources_used || [];
  const skipped = research?.sources_skipped || [];
  const usedIds = new Set(used.map(u => u.asset_id));
  const skippedIds = new Set(skipped.map(x => x.asset_id));
  const inputs = (sources || []).filter(x => x.kind !== 'brand' && x.kind !== 'build_image');
  const inferred = research ? inputs
    .filter(x => !usedIds.has(x.id) && !skippedIds.has(x.id))
    .map(x => ({
      asset_id: x.id, title: x.title || x.source_url,
      reason: x.fetch_error ? `Couldn't fetch this page — ${x.fetch_error}`
        : /^image\//.test(x.mimetype || '') ? 'Image — not read in this research run; re-run the research to include it'
        : 'Added after this research ran — re-run the research to include it',
    })) : [];
  return { used, notRead: [...skipped, ...inferred] };
}

function ResearchSources({ research, sources, collapsible = false }) {
  const [open, setOpen] = useState(!collapsible);
  const { used, notRead } = researchSourceRows(research, sources);
  if (!used.length && !notRead.length) return null;
  const summary = `${used.length} read${notRead.length ? ` · ${notRead.length} not read` : ''}`;
  return (
    <div style={{ fontSize: '12.5px', lineHeight: 1.7 }}>
      {collapsible ? (
        <button onClick={() => setOpen(o => !o)} aria-expanded={open}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: FONTS.body,
            fontSize: '12.5px', fontWeight: 700, color: notRead.length ? STATE.gate.text : INK.body }}>
          {open ? '▾' : '▸'} What the research read — {summary}
        </button>
      ) : (
        <div style={{ fontWeight: 700, color: INK.body, marginBottom: '2px' }}>What the research read — {summary}</div>
      )}
      {open && (
        <div>
          {used.map((x, i) => (
            <div key={`u${i}`} style={{ color: STATE.done.text }}>
              ✓ {x.title || x.url}
              <span style={{ color: INK.muted }}>
                {' '}— read{x.kind === 'image' ? ' (image)' : ''}{typeof x.chars === 'number' ? `, ${Math.max(1, Math.round(x.chars / 1000))}k characters` : ''}
                {x.truncated ? ' — only part of it (size limit)' : ''}
              </span>
            </div>
          ))}
          {notRead.map((x, i) => (
            <div key={`n${i}`} style={{ color: STATE.gate.text }}>
              – {x.title || x.url}
              <span style={{ color: INK.muted }}> — {x.reason}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ContentIQSetupWizard() {
  const navigate = useNavigate();
  const { projectId: routeProjectId } = useParams();
  const [params, setParams] = useSearchParams();
  const goalKey = params.get('goal');
  // Project mode (grew out of slice F's revisit mode): /contentiq/project/:id,
  // the old /contentiq/wizard/:id links, and ?project=<id> all open this
  // screen on an EXISTING project — every setup answer pre-filled from what's
  // saved, the rail fully clickable, Build/Deliver sections live.
  const projectParam = routeProjectId || params.get('project');

  // Reference data
  const [programs, setPrograms] = useState([]);
  const [clients, setClients] = useState([]);
  const [programStatuses, setProgramStatuses] = useState([]);
  const [formats, setFormats] = useState([]);

  // Answers
  const [goalPick, setGoalPick] = useState(goalKey ? 'preset' : null);
  const [programId, setProgramId] = useState(null);
  const [programComponents, setProgramComponents] = useState([]);
  const [componentId, setComponentId] = useState(null);
  // R1 (Edward, 2026-10-15, the TrackVia case): a project can cover the
  // WHOLE program — one strategy spanning several components' pages. The
  // data model always allowed it (attachment component_id NULL = whole
  // program, products derived as the union); only this wizard forced a pick.
  const [wholeProgram, setWholeProgram] = useState(false);
  const [formatId, setFormatId] = useState(null);
  // A derived product normally skips its screen — this reopens it when the PM
  // wants a different product than the component derives (Edward, 2026-10-15:
  // "the program says DA but I know they actually want DB"). The ref remembers
  // where the chip was clicked from, so Continue/Back return there.
  const [productStepOverride, setProductStepOverride] = useState(false);
  const productReturnRef = useRef(null);
  const [project, setProject] = useState(null);
  const [projectScope, setProjectScope] = useState(null);  // {programId, componentId} the project was created with
  // The goal binds the same way however it arrives (Edward, 2026-10-15:
  // "revise — the pick should be real"): ?goal= from a Home lane card, or
  // the on-screen pick here. 'full' = the whole arc, no single output
  // pinned. Either way the project keeps every output open — the goal sets
  // the near-term objective, it never locks the others out. Since 0131 the
  // project stores its lane, and the stored lane wins: reopening from Home
  // or All projects (no ?goal=) keeps it (Edward's usability pass, 2026-10-22).
  const effectiveGoal = project?.output_goal || goalKey
    || (goalPick && goalPick !== 'full' && goalPick !== 'preset' ? goalPick : null);
  const goalDef = OUTPUT_DEFS[effectiveGoal] || null;
  const [sources, setSources] = useState([]);
  const [focusDraft, setFocusDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [urlsDraft, setUrlsDraft] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteTitle, setPasteTitle] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [pickMode, setPickMode] = useState('recommend');  // 'recommend' | 'chosen'
  const [pagesPick, setPagesPick] = useState(null);
  // Two-touch products build asset PAIRS — one asset per engagement phase
  // (Edward, 2026-10-28: "could have more than one pair").
  const [pairsPick, setPairsPick] = useState(null);
  const [branchCount, setBranchCount] = useState(null);  // Branching DA: 2-4
  const [chosenIds, setChosenIds] = useState([]);
  // Plain DA with several client-chosen assets: which ONE this build features.
  // Picked on the output-spec card (Edward's mid-pass report, 2026-10-15: the
  // one-asset validation fired on a screen that had no picker — a dead end).
  const [featuredId, setFeaturedId] = useState(null);
  // Lanes vs. single build, decided AT THE ASSET STEP (Edward, 2026-10-19)
  // so every later step — and the strategy generation — already knows.
  const [buildPlan, setBuildPlan] = useState('lanes');
  // With lanes: BANT/script per lane (common for a multi-lane DA — Edward,
  // 2026-10-20) or one generic set with the asset title swapped.
  const [lanesCustom, setLanesCustom] = useState('per_lane');
  // R1 (Edward, 2026-10-15): the persona-matrix knobs (buyer scope, funnel
  // stages, page slicing) are gone — the generator reads the whole map.
  const [bantOpen, setBantOpen] = useState(false);
  // Content Syndication carries no BANT unless the PM adds it — the client's
  // own, or DemandArc's (Edward, 2026-10-23). 'none' | 'client' | 'demandarc',
  // saved as manual_input.qualification_source.
  const [qualSource, setQualSource] = useState('none');
  const [customOpen, setCustomOpen] = useState(false);
  const [bantText, setBantText] = useState('');
  const [customText, setCustomText] = useState('');
  // Project-mode setup saves stay put (Edward, 2026-09-29 walkthrough):
  // the rail is the navigation there, so Continue becomes Save.
  const [savedNote, setSavedNote] = useState('');
  // The recommended-questions picker (Edward, 2026-09-29).
  const [recOpen, setRecOpen] = useState(false);
  const [recSet, setRecSet] = useState({});
  // Opt-in / privacy language + links — required on every product's landing
  // pages (Edward, 2026-09-28), rides the deployment spec project-level.
  const [optinOpen, setOptinOpen] = useState(false);
  const [optinText, setOptinText] = useState('');  // html, like bant/custom
  // The client's one-doc copy import (slice 1, 2026-10-09) — the round-2
  // "coming soon" button made real.
  const [copyImportOpen, setCopyImportOpen] = useState(false);
  // The client's privacy policy URL, collected on its own (Edward,
  // 2026-10-09): a client that supplies no opt-in copy still must have
  // their privacy policy linked on every page.
  const [privacyUrl, setPrivacyUrl] = useState('');
  const [structure, setStructure] = useState('single');
  const [wizAsset, setWizAsset] = useState(null);
  const [wizQueue, setWizQueue] = useState([]);   // uploads still awaiting their copy question
  const [pendingCopyIds, setPendingCopyIds] = useState([]);  // dropped this visit, copy question not asked yet
  const [brandDirty, setBrandDirty] = useState(false);       // unsaved edits on the brand card (Page build)
  const [wizUrl, setWizUrl] = useState('');
  const [wizTitle, setWizTitle] = useState('');
  const [wizCopy, setWizCopy] = useState('');
  const [research, setResearch] = useState(null);
  const [mapRows, setMapRows] = useState([]);
  // Build/Deliver section data (project mode) — same shapes the panels take.
  const [personas, setPersonas] = useState([]);
  const [strategyItems, setStrategyItems] = useState([]);
  const [strategyFlow, setStrategyFlow] = useState(null);
  const [strategyMeta, setStrategyMeta] = useState({ page_plan: null, brief: null, format: null });
  const [deployInfo, setDeployInfo] = useState(null);
  const deckRail = useDeckRailStep(projectParam);

  const [step, setStep] = useState(projectParam ? 'loading' : goalKey ? 'program' : 'goal');
  // Logos & brand and Final copy live inside Page build now — any leftover
  // route to the old steps lands there.
  useEffect(() => {
    if (step === 'copy' || step === 'brand') setStep('deploy');
  }, [step]);
  const [revisit, setRevisit] = useState(!!projectParam);
  const [uploadProg, setUploadProg] = useState([]);  // per-file rows for UploadProgressList
  const uploadCancels = useRef({});  // row key -> AbortController, or 'cancelled' before it starts
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // Duplicate-name guard (2026-10-08): {fid, autoName, existing} while the
  // rename dialog is up, null otherwise.
  const [nameClash, setNameClash] = useState(null);
  const [nameDraft, setNameDraft] = useState('');
  // Phase accordion: explicit open/closed choices per phase; a phase with no
  // entry follows the default (the current step's phase is open).
  const [phaseOverrides, setPhaseOverrides] = useState({});
  const runGuard = useRef(null);
  const materialsRef = useRef(null);
  const assetFileRef = useRef(null);
  const brandFileRef = useRef(null);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/programs/`),
      axios.get(`${API}/clients/`),
      axios.get(`${API}/lookup-values/?category=program_status`),
      axios.get(`${API}/contentiq/catalog/formats`),
    ]).then(([p, c, ps, f]) => {
      if (p.status === 'fulfilled') setPrograms(p.value.data);
      if (c.status === 'fulfilled') setClients(c.value.data);
      if (ps.status === 'fulfilled') setProgramStatuses(ps.value.data);
      if (f.status === 'fulfilled') setFormats(f.value.data);
    });
  }, []);

  const loadMapRows = useCallback(() => {
    if (!projectParam) return;
    axios.get(`${API}/contentiq/projects/${projectParam}/message-map`)
      .then(r => setMapRows(r.data)).catch(() => {});
  }, [projectParam]);

  // Deployment loop state (gate + page builds + rollup) — feeds the rail's
  // done marker, the go-straight-to-Deployment nudge, and DeploymentPanel.
  const loadDeployInfo = useCallback(() => {
    if (!projectParam) return Promise.resolve(null);
    return axios.get(`${API}/contentiq/projects/${projectParam}/builds`)
      .then(r => { setDeployInfo(r.data); return r.data; }).catch(() => null);
  }, [projectParam]);
  useEffect(() => { loadDeployInfo(); }, [loadDeployInfo]);
  // Page build's gate reads deployInfo, and earlier steps (client copy,
  // questions, assets) change what it says — re-read it on every visit
  // (staging pass, 2026-10-23: it kept saying "no content assets" after the
  // asset's copy was saved).
  useEffect(() => { if (step === 'deploy') loadDeployInfo(); }, [step, loadDeployInfo]);
  // "✓ Saved" shows on the step the save moved to, then fades.
  useEffect(() => {
    if (!savedNote) return undefined;
    const t = setTimeout(() => setSavedNote(''), 4000);
    return () => clearTimeout(t);
  }, [savedNote]);

  const applyStrategyData = (data) => {
    setStrategyItems(data.items || []);
    setStrategyFlow(data.flow || null);
    setStrategyMeta({ page_plan: data.page_plan || null, brief: data.brief || null, format: data.format || null });
  };
  const loadStrategyData = useCallback(() => {
    if (!projectParam) return Promise.resolve(null);
    return axios.get(`${API}/contentiq/projects/${projectParam}/strategy`)
      .then(r => { applyStrategyData(r.data); return r.data; }).catch(() => null);
  }, [projectParam]);

  const patchStage = (updated) => {
    setProject(pj => pj && {
      ...pj,
      stages: (pj.stages || []).some(st => st.stage_number === updated.stage_number)
        ? (pj.stages || []).map(st => st.stage_number === updated.stage_number ? { ...st, ...updated } : st)
        : [...(pj.stages || []), updated],  // stage 5 (script status) is created lazily
    });
  };

  // Project-mode prefill: every answer restored from the project's saved
  // data — attachments (scope), context manual_input, sources, the stored
  // strategy brief, prior research + buyer map. All existing endpoints.
  useEffect(() => {
    if (!projectParam) return;
    (async () => {
      try {
        const pj = (await axios.get(`${API}/contentiq/projects/${projectParam}`)).data;
        const att = (pj.attachments || [])[0] || {};
        const [ctxR, srcR, stratR, mapR, compsR, buildsR, lpR] = await Promise.allSettled([
          axios.get(`${API}/contentiq/projects/${pj.id}/context`),
          axios.get(`${API}/contentiq/projects/${pj.id}/sources`),
          axios.get(`${API}/contentiq/projects/${pj.id}/strategy`),
          axios.get(`${API}/contentiq/projects/${pj.id}/message-map`),
          att.program_id
            ? axios.get(`${API}/contentiq/catalog/programs/${att.program_id}/components`)
            : Promise.reject(new Error('no program')),
          axios.get(`${API}/contentiq/projects/${pj.id}/builds`),
          axios.get(`${API}/contentiq/projects/${pj.id}/outputs/lp-copy`),
        ]);
        axios.get(`${API}/contentiq/catalog/personas`).then(r => setPersonas(r.data)).catch(() => {});
        setProject(pj);
        setProjectScope({ programId: att.program_id || null, componentId: att.component_id || null });
        setProgramId(att.program_id || null);
        setComponentId(att.component_id || null);
        setFormatId(pj.product_format_id || null);
        setGoalPick(pj.entry_mode === 'specific_output' ? 'preset' : 'full');
        if (compsR.status === 'fulfilled') setProgramComponents(compsR.value.data);
        if (srcR.status === 'fulfilled') setSources(srcR.value.data);
        const mi = ctxR.status === 'fulfilled' ? (ctxR.value.data.manual_input || {}) : {};
        setFocusDraft(mi.campaign_focus || '');
        setNotesDraft(mi.notes || '');
        // Rich since round 5 (Edward, 2026-09-29): stored html wins, plain
        // text from older saves converts on the way in.
        if (mi.client_bant_questions_html || mi.client_bant_questions) {
          setBantOpen(true);
          setBantText(mi.client_bant_questions_html || textToHtml(mi.client_bant_questions));
        }
        setQualSource(['client', 'demandarc'].includes(mi.qualification_source) ? mi.qualification_source
          : (mi.client_bant_questions_html || mi.client_bant_questions) ? 'client' : 'none');
        if (mi.client_custom_questions_html || mi.client_custom_questions) {
          setCustomOpen(true);
          setCustomText(mi.client_custom_questions_html || textToHtml(mi.client_custom_questions));
        }
        // Opt-in/privacy is ONE rich field now — links saved by the retired
        // separate rows are seeded in as anchors (absorbed on next save).
        const savedLinks = Array.isArray(mi.client_privacy_links) ? mi.client_privacy_links : [];
        if (mi.client_optin_language_html || mi.client_optin_language || savedLinks.length) {
          setOptinOpen(true);
          const esc = t => String(t).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
          const base = mi.client_optin_language_html || textToHtml(mi.client_optin_language || '');
          const linkHtml = mi.client_optin_language_html ? '' : savedLinks
            .filter(l => l && l.url)
            .map(l => `<p><a href="${esc(l.url)}">${esc(l.label || l.url)}</a></p>`).join('');
          setOptinText(sanitizeCopyHtml((base || '') + linkHtml));
        }
        setPrivacyUrl(mi.client_privacy_policy_url || '');
        // research_profile stores the whole research blob (company_profile,
        // personas, …) — the same shape POST /research returns.
        const profile = ctxR.status === 'fulfilled' ? ctxR.value.data.research_profile : null;
        if (profile && typeof profile === 'object') setResearch(profile);
        if (mapR.status === 'fulfilled') setMapRows(mapR.value.data || []);
        const brief = stratR.status === 'fulfilled' ? (stratR.value.data.brief || {}) : {};
        if (Array.isArray(brief.asset_ids) && brief.asset_ids.length) {
          setPickMode('chosen'); setChosenIds(brief.asset_ids);
        } else if (brief.asset_count) {
          setPickMode('recommend'); setPagesPick(brief.asset_count);
        }
        if (brief.structure === 'per_persona') setStructure('per_persona');
        if (stratR.status === 'fulfilled') applyStrategyData(stratR.value.data);
        // Land by request (?section= / legacy ?stage=), else at the FURTHEST
        // in-progress stage: builds in flight or page copy exists -> Page
        // build, a strategy stands -> the strategy, else the plan
        // (Edward's walkthrough round, 2026-09-29 — a syndication project
        // with pages used to resume on the Build plan as if nothing had
        // happened past setup).
        const hasStrategy = stratR.status === 'fulfilled'
          && !!(stratR.value.data.flow || (stratR.value.data.items || []).length);
        const hasBuilds = buildsR.status === 'fulfilled'
          && (buildsR.value.data.builds || []).length > 0;
        const hasPages = lpR.status === 'fulfilled'
          && (lpR.value.data.pages || []).length > 0;
        if (buildsR.status === 'fulfilled') setDeployInfo(buildsR.value.data);
        const section = params.get('section');
        const legacyStage = { 1: 'context', 2: 'buyermap', 3: 'strategy-view', 4: 'deploy' }[Number(params.get('stage'))];
        const bySection = {
          scope: 'program', product: 'product', context: 'context', assets: 'assets',
          questions: 'questions', brand: 'deploy', review: 'review',
          research: 'research-view', personas: 'buyermap', strategy: 'strategy-view',
          copy: 'deploy', script: 'strategy-view', deploy: 'deploy', launch: 'launch', deck: 'deck',
        }[section];
        // Skip-path (LP-only) projects never land on a strategy-ish step:
        // when the deployment gate is already ready (every asset carries
        // client copy, no strategy flow), Page build — with the page copy's
        // assemble-vs-generate guidance — is the sensible landing even before pages
        // exist (Edward's staging feedback, 2026-10-08). And once the
        // package is complete (or any launch milestone is stamped), the
        // project is in phase 4 — land there, not among the page cards.
        const gateReady = buildsR.status === 'fulfilled'
          && !!buildsR.value.data.gate?.ready;
        const deployData = buildsR.status === 'fulfilled' ? buildsR.value.data : null;
        const inLaunchPhase = !!deployData && (
          deployData.rollup?.all_accepted
          || Object.values(deployData.milestones || {}).some(Boolean));
        setStep(bySection || legacyStage
          || (inLaunchPhase ? 'launch' : hasBuilds ? 'deploy' : hasPages ? 'deploy'
              : hasStrategy ? 'strategy-view'
              : gateReady ? 'deploy' : 'review'));
      } catch (e) {
        setRevisit(false);
        setErr('Could not load that project — starting a fresh setup instead.');
        setStep(goalKey ? 'program' : 'goal');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectParam]);

  const clientName = (id) => clients.find(c => c.id === id)?.name || '';
  const program = programs.find(pr => pr.id === programId) || null;
  const component = programComponents.find(c => c.component_id === componentId) || null;
  const fmt = formats.find(f => f.id === formatId) || null;

  // A designated flow-role asset (0124 — DC's Stage-2 call offer) is never
  // in the FEATURED pool: not choosable, not counted, no LP-copy question.
  const clientAssets = sources.filter(x => x.is_promotable && x.kind !== 'brand' && !x.flow_role);
  const offerAssets = sources.filter(x => x.is_promotable && x.kind !== 'brand' && x.flow_role === 'stage2_offer');
  const promotableAll = sources.filter(x => x.is_promotable && x.kind !== 'brand');
  const brandFiles = sources.filter(x => x.kind === 'brand');
  const researchSources = sources.filter(x => !x.is_promotable && x.kind !== 'brand');
  // Only formats whose recipe declares program stages (Demand Cascade) ask
  // what stage an asset is "for" (Edward, 2026-10-20).
  const declaresStages = (fmt?.strategy_recipe?.touchpoints || []).some(tp => tp && tp.program_stage);
  const chosenAssets = chosenIds.map(id => clientAssets.find(a => a.id === id)).filter(Boolean);
  // Recipe facts (0105): copy_scope says who owns each page's copy. A page
  // with 'shared' scope is DemandArc-written — writing copy means research,
  // so such formats (the Deal Accelerators) never fully skip.
  const fmtPages = fmt?.page_structure || [];
  const hasOursPage = fmtPages.some(pg => pg && pg.copy_scope === 'shared');
  const isBranching = !!fmt?.has_branching;
  const isPlainDA = !!fmt && !isBranching && hasOursPage;
  const isTwoTouch = !!fmt?.strategy_recipe?.touches;
  const pairChoices = Math.max(1, Math.min(4, Math.floor(clientAssets.length / 2)));
  const chosenPairs = Math.max(1, Math.min(4, Math.floor(chosenIds.length / 2)));
  // A plain DA featuring one of several chosen assets: the featured pick (made
  // on the output-spec card) narrows the build without unpicking the client's
  // real choice list. Stale picks (asset unchecked later) simply don't count.
  const needsFeaturedPick = isPlainDA && pickMode === 'chosen' && chosenIds.length > 1
    && buildPlan === 'single';
  const lanesPlanned = isPlainDA && pickMode === 'chosen' && chosenIds.length > 1
    && buildPlan === 'lanes';
  const featuredValid = needsFeaturedPick && chosenIds.includes(featuredId);
  const specAssetIds = featuredValid ? [featuredId] : chosenIds;
  const assemblyPossible = pickMode === 'chosen' && chosenAssets.length > 0
    && chosenAssets.every(a => a.has_client_copy) && !hasOursPage;
  const fullySupplied = assemblyPossible;
  // Lane 3 — landing pages from copy written outside ContentIQ (Edward,
  // 2026-10-22): every asset is a page, nothing is researched or written.
  const isPageBuildLane = effectiveGoal === 'pagebuild';
  // A product whose recipe places no BANT anywhere (the Content Syndication
  // family) asks whether to add it at all.
  const bantPlacement = fmt?.strategy_recipe?.questions?.bant?.placement;
  const bantOptional = !!fmt && !fmt.has_branching && !(Array.isArray(bantPlacement)
    ? bantPlacement.length : bantPlacement && Object.keys(bantPlacement).length);
  const pageBuildMissingCopy = isPageBuildLane ? clientAssets.filter(a => !a.has_client_copy) : [];
  // Lane 3 never asks whether the client chose the assets: every asset is
  // one of the pages.
  const clientAssetKey = clientAssets.map(a => a.id).join(',');
  useEffect(() => {
    if (!isPageBuildLane) return;
    setPickMode('chosen');
    setChosenIds(clientAssets.map(a => a.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageBuildLane, clientAssetKey]);

  const derivedProducts = componentId
    ? programComponents.filter(c => c.component_id === componentId).map(c => c.product_name).filter(Boolean)
    : [...new Set(programComponents.map(c => c.product_name).filter(Boolean))];
  const derivedFormat = deriveFormat(formats, derivedProducts);
  // The component's product was changed in NeTrA after this project was
  // made (Edward + Jon's walkthrough, 2026-10-28) — the server says what it
  // derives now; the PM switches with one click instead of starting over.
  const productChanged = !!(project && project.derived_format_id
    && project.derived_format_id !== project.product_format_id);
  const switchToDerived = async () => {
    setBusy(true); setErr('');
    try {
      const res = await axios.put(`${API}/contentiq/projects/${project.id}`,
        { product_format_id: project.derived_format_id });
      setProject(res.data);
      setFormatId(res.data.product_format_id);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not switch the product.');
    } finally { setBusy(false); }
  };

  // The step sequence, derived from answers (screens that don't apply vanish).
  // The product screen only appears when the component's product does NOT
  // resolve it (Edward, 2026-08-25: "you should know the product from the
  // component") — when derived, it's confirmed silently and shown as a chip.
  const steps = [
    ...(goalKey || revisit ? [] : ['goal']),
    'program',
    ...(programComponents.length > 1 ? ['component'] : []),
    ...(derivedFormat && !productStepOverride ? [] : ['product']),
    'assets',
    ...(clientAssets.length > 0 && !isPageBuildLane ? ['count'] : []),
    'questions',
    // Company & campaign context feeds RESEARCH — when everything is client-
    // supplied on a skippable recipe there is nothing to research, so the
    // step disappears entirely (Edward, 2026-08-27).
    ...(fullySupplied || isPageBuildLane ? [] : ['context']),
    'review',
  ];

  // Lane 3 can't serve a product with a DemandArc-written page (the Deal
  // Accelerators) until strategy import exists — switch the project to the
  // Landing page copy lane in place, answers kept.
  const switchLane = async (goal) => {
    setErr('');
    if (project) {
      try {
        const r = await axios.put(`${API}/contentiq/projects/${project.id}`, { output_goal: goal });
        setProject(r.data);
      } catch (e) { setErr('Could not switch this project\u2019s lane — try again.'); return; }
    }
    const next = new URLSearchParams(params);
    next.set('goal', goal);
    setParams(next, { replace: true });
  };
  // Landing page build serves products whose pages all carry the client's
  // own copy (the Content Syndication family). A product with a page
  // DemandArc writes, or a branching flow (DA, DB, DB+, DC), is caught as soon
  // as the program shows it (Edward, 2026-10-23) — its copy comes out of the
  // content strategy, so it moves to Landing page copy.
  const laneFormat = fmt || derivedFormat;
  const laneNeedsStrategy = !!laneFormat && (!!laneFormat.has_branching
    || (laneFormat.page_structure || []).some(pg => pg && pg.copy_scope === 'shared'));
  const laneDeadEnd = isPageBuildLane && laneNeedsStrategy
    && ['program', 'component', 'product', 'assets', 'count', 'questions', 'context', 'review'].includes(step);

  // Continue always goes to the next screen in `steps`, so a lane that skips
  // a screen (Landing page build skips count and context) never lands on it.
  const stepAfter = (from) => steps[steps.indexOf(from) + 1] || 'review';

  const reloadSources = async (pid) => {
    const r = await axios.get(`${API}/contentiq/projects/${pid || project.id}/sources`);
    setSources(r.data);
    return r.data;
  };

  // After a copy import lands: the asset list changed (copy markers, maybe a
  // new linked asset), and appended questions must reach the Questions step's
  // own editor state — it only reads context at load time otherwise.
  const copyImportApplied = async (res) => {
    try { await reloadSources(); } catch { /* next load catches up */ }
    if (res?.questions_appended) {
      try {
        const r = await axios.get(`${API}/contentiq/projects/${project.id}/context`);
        const mi = r.data.manual_input || {};
        if (mi.client_custom_questions_html || mi.client_custom_questions) {
          setCustomOpen(true);
          setCustomText(mi.client_custom_questions_html || textToHtml(mi.client_custom_questions));
        }
      } catch { /* the questions step re-reads on next load anyway */ }
    }
  };

  const saveContext = async (extraFields) => {
    const cur = await axios.get(`${API}/contentiq/projects/${project.id}/context`)
      .then(r => r.data.manual_input || {}).catch(() => ({}));
    await axios.put(`${API}/contentiq/projects/${project.id}/context`, {
      manual_input: { ...cur, ...extraFields },
    });
  };

  // Concurrent per-file uploads with live progress (staging round 1: five
  // serial uploads showed one bare "uploading" for minutes). Three at a time
  // — the backend runs each upload's S3 put + text extraction off its event
  // loop now, so parallel requests genuinely overlap. Results come back in
  // DROP order regardless of completion order, so the copy-question queue
  // stays in the order the PM dropped the files. A file over the size cap
  // fails on the spot and is never sent (staging 2026-10-29: a 52.7 MB ebook
  // uploaded for minutes, then read "HTTP 413").
  const uploadBatch = async (files, extra) => {
    const rows = files.map((f, i) => {
      const tooBig = oversizeReason(f, CIQ_ASSET_MAX_BYTES);
      return {
        key: `${Date.now()}-${i}`, name: f.name, pct: 0,
        state: tooBig ? 'failed' : 'queued', err: tooBig,
      };
    });
    uploadCancels.current = {};
    setUploadProg(rows);
    const patch = (i, up) => setUploadProg(list =>
      list.map((r, j) => (j === i ? { ...r, ...up } : r)));
    const results = new Array(files.length);
    let cursor = 0;
    const worker = async () => {
      for (;;) {
        const i = cursor++;
        if (i >= files.length) return;
        const f = files[i];
        const key = rows[i].key;
        if (rows[i].state === 'failed') {
          results[i] = { ok: false, name: f.name, err: rows[i].err, tooLarge: true };
          continue;
        }
        if (uploadCancels.current[key] === 'cancelled') {
          results[i] = { ok: false, cancelled: true };
          continue;
        }
        const controller = new AbortController();
        uploadCancels.current[key] = controller;
        patch(i, { state: 'uploading' });
        try {
          const fd = new FormData();
          fd.append('file', f);
          Object.entries(extra || {}).forEach(([k, v]) => fd.append(k, v));
          const res = await axios.post(`${API}/contentiq/projects/${project.id}/assets`, fd, {
            signal: controller.signal,
            onUploadProgress: (ev) => {
              const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
              // Bytes fully sent but no response yet = the server is
              // sniffing/storing/extracting — say so instead of sitting at 100%.
              patch(i, pct >= 100 ? { pct: 100, state: 'processing' } : { pct, state: 'uploading' });
            },
          });
          patch(i, { pct: 100, state: 'done' });
          results[i] = { ok: true, data: res.data };
        } catch (e) {
          if (axios.isCancel(e) || controller.signal.aborted) {
            patch(i, { state: 'cancelled' });
            results[i] = { ok: false, cancelled: true };
            continue;
          }
          const msg = uploadErrorText(e, CIQ_ASSET_MAX_BYTES);
          patch(i, { state: 'failed', err: msg });
          results[i] = { ok: false, name: f.name, err: msg, tooLarge: e.response?.status === 413 };
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, files.length) }, worker));
    const failedRows = results.filter(r => r && !r.ok && !r.cancelled);
    return {
      uploaded: results.filter(r => r?.ok).map(r => r.data),
      failed: failedRows.map(r => `${r.name} — ${r.err}`),
      // Appended once to the caller's error line when any file was too big.
      advice: failedRows.some(r => r.tooLarge) ? ` ${oversizeAdvice(CIQ_ASSET_MAX_BYTES)}` : '',
    };
  };

  // Cancel one file: a queued file never starts, a sending one is aborted.
  const cancelUpload = (key) => {
    const c = uploadCancels.current[key];
    if (c && typeof c.abort === 'function') c.abort();
    else uploadCancels.current[key] = 'cancelled';
    setUploadProg(list => list.map(r => (r.key === key ? { ...r, state: 'cancelled' } : r)));
  };

  const uploadFiles = async (files, extra) => {
    setBusy(true); setErr('');
    const { failed, advice } = await uploadBatch(files, extra || {});
    await reloadSources();
    if (failed.length) {
      setErr(`Couldn't upload ${failed.length} file${failed.length === 1 ? '' : 's'}: ${failed.join('; ')}.${advice}`);
    }
    setBusy(false);
  };

  // ── Per-step continue handlers ─────────────────────────────────────────

  const continueProgram = async () => {
    if (!programId) { setErr('Pick the program.'); return; }
    setErr('');
    if (programComponents.length > 1) { setStep('component'); return; }
    await advanceFromScope();
  };

  const pickProgram = async (v) => {
    const id = Number(v);
    setProgramId(id);
    setComponentId(null);
    // A product picked for the previous program must not decide this one's
    // lane check (staging pass, 2026-10-23).
    if (!project) setFormatId(null);
    // A whole-program choice belongs to the program it was made on — don't
    // carry it onto the next program's scope step (found in the R1 pass).
    setWholeProgram(false);
    setErr('');
    try {
      const r = await axios.get(`${API}/contentiq/catalog/programs/${id}/components`);
      setProgramComponents(r.data);
      if (r.data.length === 1) setComponentId(r.data[0].component_id);
    } catch (e) { setErr('Could not load that program’s components.'); }
  };

  const _autoName = (fid) => {
    const f = formats.find(x => x.id === fid);
    return `${component?.component_name || program?.program_name || 'Content project'} — ${f?.format_name || 'Content'}`;
  };

  const _createProject = async (fid, name) => {
    const res = await axios.post(`${API}/contentiq/projects/`, {
      name,
      attachments: [{ program_id: programId, component_id: componentId }],
      entry_mode: goalDef ? 'specific_output' : 'full_engagement',
      output_goal: goalDef ? effectiveGoal : null,
      product_format_id: fid,
    });
    setProject(res.data);
    setProjectScope({ programId, componentId });
  };

  // Duplicate names caused real confusion on staging (2026-10-08: two live
  // Uber projects with the identical auto-name, Edward couldn't tell whose
  // test URLs he was looking at). Before creating, check the live list and
  // give the PM the chance to rename — suggesting the first free "(2)"-style
  // variant — rather than silently minting a twin.
  const _firstFreeName = (base, names) => {
    const taken = new Set(names.map(n => (n || '').trim().toLowerCase()));
    let i = 2;
    while (taken.has(`${base} (${i})`.toLowerCase())) i += 1;
    return `${base} (${i})`;
  };

  const _createOrWarn = async (fid) => {
    const autoName = _autoName(fid);
    let existing = [];
    try {
      existing = (await axios.get(`${API}/contentiq/projects/`)).data || [];
    } catch { /* list unavailable — create as before rather than blocking */ }
    const clash = existing.filter(
      p => (p.name || '').trim().toLowerCase() === autoName.trim().toLowerCase());
    if (clash.length) {
      setNameClash({ fid, autoName, existing: clash });
      setNameDraft(_firstFreeName(autoName, existing.map(p => p.name)));
      return false;
    }
    await _createProject(fid, autoName);
    return true;
  };

  const proceedToAssets = async (fid) => {
    setBusy(true); setErr('');
    try {
      if (project && projectScope
          && (projectScope.programId !== programId || projectScope.componentId !== componentId)) {
        // Scope changed on a Back-edit — the old project (test data by
        // Edward's rule) is discarded and a fresh one created. On a saved
        // project opened from the list that is a real deletion, so it asks
        // first (consistency pass, 2026-10-22: it hid behind "Continue →").
        if (projectParam && !window.suppressConfirm && !window.confirm(
          `Changing the program or component starts a new project and deletes “${project.name}”. `
          + 'Its uploaded files are kept and an admin can restore it. Continue?')) {
          return;
        }
        await axios.delete(`${API}/contentiq/projects/${project.id}`).catch(() => {});
        setProject(null); setProjectScope(null); setSources([]);
        if (!(await _createOrWarn(fid))) return;
      } else if (project) {
        if (project.product_format_id !== fid) {
          const res = await axios.put(`${API}/contentiq/projects/${project.id}`,
            { product_format_id: fid });
          setProject(res.data);
        }
      } else {
        if (!(await _createOrWarn(fid))) return;
      }
      setStep('assets');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not create the project.');
    } finally { setBusy(false); }
  };

  // The clash dialog's confirm: create under whichever name stands in the
  // box — the suggested variant, the PM's own edit, or (their call) even
  // the duplicate itself.
  const confirmNamedCreate = async () => {
    const name = (nameDraft || '').trim();
    if (!name) { setErr('Give the project a name.'); return; }
    setBusy(true); setErr('');
    try {
      await _createProject(nameClash.fid, name);
      setNameClash(null);
      setStep('assets');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not create the project.');
    } finally { setBusy(false); }
  };

  // Advance out of the scope steps: derived product skips its screen.
  const advanceFromScope = async () => {
    if (derivedFormat) {
      setFormatId(derivedFormat.id);
      await proceedToAssets(derivedFormat.id);
    } else {
      setStep('product');
    }
  };

  // Pre-select the derived format when the (ambiguous-case) step opens.
  useEffect(() => {
    if (step === 'product' && !formatId && derivedFormat) setFormatId(derivedFormat.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, derivedFormat?.id]);

  const continueProduct = async () => {
    if (!formatId) { setErr('Pick the DemandArc product — it shapes the whole plan.'); return; }
    const returnTo = productReturnRef.current;
    if (returnTo && project) {
      // Product-change from a later step: save the new format and go back to
      // where the chip was clicked, not to the assets step.
      productReturnRef.current = null;
      setBusy(true); setErr('');
      try {
        if (project.product_format_id !== formatId) {
          const res = await axios.put(`${API}/contentiq/projects/${project.id}`,
            { product_format_id: formatId });
          setProject(res.data);
        }
        setStep(returnTo);
      } catch (e) {
        setErr(e.response?.data?.detail || 'Could not change the product.');
      } finally { setBusy(false); }
      return;
    }
    productReturnRef.current = null;
    await proceedToAssets(formatId);
  };

  // The format chip in the header opens the product screen for an override —
  // only while the strategy hasn't been built from it yet.
  const canChangeProduct = !!fmt && !busy
    && ['assets', 'count', 'questions', 'context', 'review', 'research-review'].includes(step);
  const openProductChange = () => {
    productReturnRef.current = step;
    setProductStepOverride(true);
    setErr('');
    setStep('product');
  };

  const addUrls = async () => {
    const urls = urlsDraft.split(/[\s,]+/).map(u => u.trim()).filter(Boolean);
    if (!urls.length) return;
    setBusy(true);
    const failures = [];
    for (const u of urls) {
      try {
        await axios.post(`${API}/contentiq/projects/${project.id}/sources`, { source_url: u });
      } catch (e) { failures.push(u); }
    }
    setUrlsDraft(failures.join('\n'));
    setErr(failures.length ? `Not added: ${failures.join(' · ')}` : '');
    await reloadSources();
    setBusy(false);
  };

  const addPaste = async () => {
    if (!pasteText.trim()) return;
    try {
      await axios.post(`${API}/contentiq/projects/${project.id}/sources`,
        { title: pasteTitle.trim() || null, text_content: pasteText });
      setPasteOpen(false); setPasteTitle(''); setPasteText(''); setErr('');
      await reloadSources();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not add pasted material.'); }
  };

  const continueContext = async () => {
    setBusy(true); setErr('');
    try {
      await saveContext({ campaign_focus: focusDraft.trim(), notes: notesDraft.trim() });
      // Revisit mode advances too — same consistency fix as continueQuestions.
      setSavedNote('context');
      setStep('review');
    } catch (e) { setErr('Could not save the context.'); }
    finally { setBusy(false); }
  };

  const onAssetFiles = async (files) => {
    setBusy(true); setErr('');
    // Duplicate guard (Edward, 2026-09-23: a failed batch retried into a
    // duplicate asset) — a file whose name is already on the project needs
    // an explicit OK before it uploads again.
    const existing = new Set(sources.map(s => (s.original_filename || s.title || '').toLowerCase()));
    let toSend = [...files];
    const dupes = toSend.filter(f => existing.has(f.name.toLowerCase()));
    if (dupes.length && !window.suppressConfirm) {
      const names = dupes.map(f => `“${f.name}”`).join(', ');
      if (!window.confirm(`${names} ${dupes.length === 1 ? 'is' : 'are'} already on this project. Upload again anyway? (That creates a duplicate — Cancel skips ${dupes.length === 1 ? 'it' : 'them'} and uploads the rest.)`)) {
        toSend = toSend.filter(f => !existing.has(f.name.toLowerCase()));
      }
    }
    // Per-file upload: one failure must never hide the files that DID land.
    // (Edward's 4-assets-from-3-drops: a mid-batch failure left an invisible
    // success behind, the retry duplicated it, and the orphan never got its
    // copy question.) Dropping no longer starts the copy questions (Edward,
    // 2026-10-22: he gathers assets over several drops) — every asset still
    // gets its question, from the "Answer the copy question" button or its
    // own row's tag, when he's ready.
    const { uploaded, failed, advice } = await uploadBatch(toSend, { is_promotable: 'true' });
    await reloadSources();  // always: successes must show even when some failed
    if (uploaded.length) setPendingCopyIds(ids => [...ids, ...uploaded.map(a => a.id)]);
    if (failed.length) {
      setErr(`Couldn't upload ${failed.length} file${failed.length === 1 ? '' : 's'}: ${failed.join('; ')}. ` +
        'Anything that did upload is already in the list above — re-drop only the failed ones.' + advice);
    }
    setBusy(false);
  };

  // The copy question, walked asset by asset over the assets dropped this
  // visit that haven't been asked yet ("we'll write it" stores no copy, so
  // the server can't tell answered from unasked — older assets keep their
  // row's "+ client copy" tag instead).
  const copyUnanswered = pendingCopyIds
    .map(id => clientAssets.find(a => a.id === id))
    .filter(Boolean);
  const startCopyQuestions = () => {
    if (!copyUnanswered.length) return;
    setErr('');
    setWizAsset(copyUnanswered[0]);
    setWizQueue(copyUnanswered.slice(1));
    setWizCopy('');
    setPendingCopyIds([]);
  };

  // 0124: designate what stage an asset is "for" (DC's Stage-2 call offer).
  // Designating also un-chooses it — an offer asset is never a page/path.
  const setAssetFlowRole = async (a, role) => {
    try {
      await axios.patch(`${API}/contentiq/projects/${project.id}/assets/${a.id}`,
        { flow_role: role });
      setSources(ss => ss.map(x => (x.id === a.id ? { ...x, flow_role: role || null } : x)));
      if (role) setChosenIds(ids => ids.filter(x => x !== a.id));
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not update the asset');
    }
  };

  const removeWizAsset = async (a) => {
    if (!window.suppressConfirm &&
        !window.confirm(`Remove “${a.title}” from this project?`)) return;
    try {
      const path = a.asset_type === 'source_upload' ? 'assets' : 'sources';
      await axios.delete(`${API}/contentiq/projects/${project.id}/${path}/${a.id}`);
      setPendingCopyIds(ids => ids.filter(x => x !== a.id));
      if (wizAsset && wizAsset.id === a.id) {
        // Removing the asset currently being asked about advances the queue.
        const rest = wizQueue.filter(x => x.id !== a.id);
        setWizAsset(rest[0] || null);
        setWizQueue(rest.slice(1));
        setWizCopy('');
      } else {
        setWizQueue(q => q.filter(x => x.id !== a.id));
      }
      await reloadSources();
    } catch (e) { setErr('Could not remove the asset.'); }
  };

  const addUrlAsset = async () => {
    const u = wizUrl.trim();
    if (!u) return;
    setBusy(true); setErr('');
    try {
      const res = await axios.post(`${API}/contentiq/projects/${project.id}/sources`,
        { source_url: u, title: wizTitle.trim() || null, is_promotable: true });
      setWizAsset(res.data); setWizCopy(''); setWizUrl(''); setWizTitle('');
      await reloadSources();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not add the URL asset.'); }
    finally { setBusy(false); }
  };

  const saveAssetCopy = async (html) => {
    // A fresh asset with no text = "we'll write it", a no-op. But an asset
    // that ALREADY carries copy must be actively cleared — the wrong-copy
    // trap from Edward's Indeed SMB trial: once saved, the old badge gave no
    // way back in. Rich since 0117: sanitized HTML is what's stored; the
    // backend derives the plain text from it (and re-sanitizes).
    const sanitized = sanitizeCopyHtml(html);
    if (htmlToText(sanitized).trim() || wizAsset.has_client_copy) {
      setErr('');
      try {
        await axios.patch(`${API}/contentiq/projects/${project.id}/assets/${wizAsset.id}`,
          { client_copy_html: sanitized });
        await reloadSources();
      } catch (e) {
        // Stay on this asset so nothing typed is lost (staging pass, 2026-10-23:
        // a failed save used to do nothing at all).
        setErr(e.response?.status >= 500 || !e.response
          ? 'Could not save the copy — DemandArc may be restarting. Try again in a moment.'
          : (e.response?.data?.detail || 'Could not save the copy.'));
        return;
      }
    }
    // Advance to the next uploaded asset still awaiting its copy question.
    setWizAsset(wizQueue[0] || null);
    setWizQueue(q => q.slice(1));
    setWizCopy('');
  };

  // A project that assembles from the client's copy generates nothing, so
  // "DemandArc writes them" starts from the recommended set as editable text
  // (services/lp_copy.py prints it as DemandArc's proposed set). A generated
  // strategy writes all four itself (strategy.project_recipe), so the box
  // stays closed there.
  const qualAsText = isPageBuildLane || assemblyPossible;
  const pickQualSource = (src) => {
    // Untouched recommended text is DemandArc's, not the client's — moving to
    // "the client's own" starts an empty box (staging pass, 2026-10-23).
    if (src === 'client' && qualSource === 'demandarc'
      && htmlToText(bantText).trim() === RECOMMENDED_TEXT) setBantText('');
    setQualSource(src);
    if (src === 'client') { setBantOpen(true); return; }
    if (src === 'demandarc' && qualAsText) {
      setBantOpen(true);
      if (!htmlToText(bantText).trim()) {
        setBantText(sanitizeCopyHtml(RECOMMENDED_QUESTIONS.map(([, , text]) => textToHtml(text)).join('')));
      }
      return;
    }
    setBantOpen(false);
  };

  // The content plan's line about questions — what this project will really do.
  const planQuestions = () => {
    const custom = !!htmlToText(customText).trim();
    if (bantOptional) {
      if (qualSource === 'demandarc') return 'DemandArc\u2019s qualification questions, marked proposed for the client to confirm';
      if (qualSource === 'client') return 'the client\u2019s own qualification questions, verbatim';
      return custom ? 'the client\u2019s custom questions and no qualification questions'
        : 'no qualification questions — the form asks for contact details only';
    }
    return htmlToText(bantText).trim() || custom ? 'the client\u2019s questions, verbatim' : 'proposed BANT questions';
  };

  const insertRecommended = () => {
    const picked = RECOMMENDED_QUESTIONS.filter(([k]) => recSet[k]).map(([, , text]) => textToHtml(text));
    if (!picked.length) return;
    setBantOpen(true);
    setBantText(t => sanitizeCopyHtml((htmlToText(t).trim() ? t : '') + picked.join('')));
    setRecOpen(false);
    setRecSet({});
  };

  const continueQuestions = async () => {
    setBusy(true); setErr('');
    try {
      await saveContext({
        // Sanitized client-side too; the server re-sanitizes and DERIVES the
        // plain-text twins (client_bant_questions etc.), so every existing
        // text consumer keeps working. Links live inline in the html now —
        // the old separate links list retires on save (its rows were seeded
        // into the opt-in field at load).
        client_bant_questions_html: bantOpen ? sanitizeCopyHtml(bantText) : '',
        ...(bantOptional ? { qualification_source: qualSource } : {}),
        client_custom_questions_html: customOpen ? sanitizeCopyHtml(customText) : '',
        client_optin_language_html: optinOpen ? sanitizeCopyHtml(optinText) : '',
        client_privacy_policy_url: privacyUrl.trim(),
        client_privacy_links: [],
      });
      // Revisit mode advances too (Edward, 2026-10-20: stepping forward
      // through assets/count then hitting a save-in-place wall here read
      // as a bug — the forward walk must be consistent). The saved note
      // still shows if they come back via the rail.
      // The Page build gate reads deployInfo — reload it, or it keeps
      // asking for a privacy link that is already saved (2026-10-23).
      await loadDeployInfo();
      setSavedNote('questions');
      setStep(stepAfter('questions'));
    } catch (e) { setErr('Could not save the questions.'); }
    finally { setBusy(false); }
  };

  // The output spec (research-review's "what does my output need to be?") —
  // validated, saved for the workspace, then the strategy build starts.
  const startStrategyWithSpec = async () => {
    // Same guard as the Strategy step's own regenerate — this path is two
    // clicks from Home, and an approved strategy may already be with the
    // client or the build team.
    if ((strategyFlow || strategyItems.length) && !window.confirm(
      'Rebuild the strategy? The current strategy (and its AI-written rows) is replaced. '
      + 'Anything already sent to the client or the build team stays as sent.')) return;
    if (isBranching && pickMode === 'recommend' && !branchCount) {
      setErr('Pick how many branches the Deal Accelerator routes to.'); return;
    }
    if (needsFeaturedPick && !featuredValid) {
      setErr(`A single ${fmt.format_name} build features exactly one asset — pick the featured asset below, or switch to lanes.`); return;
    }
    if (isTwoTouch && pickMode === 'recommend' && !pairsPick) {
      setErr('Pick how many asset pairs the output needs.'); return;
    }
    if (!isTwoTouch && !isBranching && !isPlainDA && pickMode === 'recommend' && clientAssets.length > 0 && !pagesPick) {
      setErr('Pick how many pages the output needs.'); return;
    }
    if (pickMode === 'chosen' && !chosenIds.length) { setErr('Pick the client’s chosen assets.'); return; }
    setErr('');
    runGuard.current = null;
    setStep('strategy-run');
  };

  // ── The two-phase build ────────────────────────────────────────────────

  const startBuild = (useAssembly) => {
    setErr('');
    setStep(useAssembly ? 'strategy-run' : 'research-run');
    runGuard.current = null;
  };

  // The skip path's second exit: no model call at all — the pages are
  // assembled verbatim from the client's supplied ingredients.
  const assembleToPages = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/contentiq/projects/${project.id}/outputs/assemble-pages`,
        { asset_ids: chosenIds });
      if (projectParam) { setStep('deploy'); }
      else {
        navigate(`/contentiq/project/${project.id}?section=deploy${effectiveGoal ? `&goal=${effectiveGoal}` : ''}`, { replace: true });
      }
    } catch (e) { setErr(e.response?.data?.detail || 'Could not assemble the pages.'); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (step === 'research-run' && runGuard.current !== 'research') {
      runGuard.current = 'research';
      (async () => {
        try {
          const res = await axios.post(`${API}/contentiq/projects/${project.id}/research`,
            null, { timeout: 240000 });
          setResearch(res.data);
          const rows = await axios.get(`${API}/contentiq/projects/${project.id}/message-map`)
            .then(r => r.data).catch(() => []);
          setMapRows(rows);
          setStep('research-review');
        } catch (e) {
          setErr(e.response?.data?.detail || 'Research failed — your answers are saved; try again.');
          setStep('review');
        }
      })();
    }
    if (step === 'strategy-run' && runGuard.current !== 'strategy') {
      runGuard.current = 'strategy';
      (async () => {
        try {
          await axios.post(`${API}/contentiq/projects/${project.id}/strategy/generate`, {
            structure,
            interactive: !!fmt?.has_branching,
            asset_ids: pickMode === 'chosen' && specAssetIds.length ? specAssetIds : undefined,
            lanes: isPlainDA && pickMode === 'chosen' && chosenIds.length > 1
              ? (buildPlan === 'lanes'
                  ? chosenIds.map(id => {
                      const a = clientAssets.find(x => x.id === id);
                      return { name: (a && (a.title || a.filename)) || null, asset_ids: [id] };
                    })
                  : [])
              : undefined,
            lanes_custom: isPlainDA && pickMode === 'chosen' && chosenIds.length > 1
              && buildPlan === 'lanes' ? lanesCustom : undefined,
            // DA recipes decide their own asset counts server-side; only the
            // page-per-asset formats (CS) send the picked page count.
            asset_count: pickMode === 'recommend' && !isBranching && !isPlainDA && !isTwoTouch
              ? (pagesPick || undefined) : undefined,
            // Two-touch: the pair count decides the asset count server-side.
            pairs: isTwoTouch
              ? (pickMode === 'chosen' ? chosenPairs : (pairsPick || undefined))
              : undefined,
            branch_count: isBranching
              ? (pickMode === 'chosen'
                  ? (chosenIds.length >= 2 && chosenIds.length <= 4 ? chosenIds.length : undefined)
                  : (branchCount || undefined))
              : undefined,
            assembly: step === 'strategy-run' && assemblyPossible && !research ? true : undefined,
          }, { timeout: 240000 });
          if (projectParam) {
            // Already on the project screen — open the strategy in place.
            await loadStrategyData();
            setStep('strategy-view');
          } else {
            // Fresh setup: move onto the project's permanent URL; the mount
            // there reloads everything and lands on the strategy.
            navigate(`/contentiq/project/${project.id}?section=strategy${effectiveGoal ? `&goal=${effectiveGoal}` : ''}`, { replace: true });
          }
        } catch (e) {
          setErr(e.response?.data?.detail || 'The strategy build failed — try again.');
          setStep(research ? 'research-review' : 'review');
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ── Footer wiring ──────────────────────────────────────────────────────

  const goBack = () => {
    setErr('');
    if (step === 'product' && productReturnRef.current) {
      // Back cancels a product-change visit: return where the chip was
      // clicked, leaving the format as it was.
      const returnTo = productReturnRef.current;
      productReturnRef.current = null;
      setFormatId(project?.product_format_id ?? formatId);
      setStep(returnTo);
      return;
    }
    if (step === 'research-review') { setStep('review'); return; }
    const idx = steps.indexOf(step);
    if (idx > 0) setStep(steps[idx - 1]);
  };

  const FOOTERS = {
    goal: { continueLabel: 'Continue →', onContinue: () => (goalPick ? setStep('program') : setErr('Pick one.')), continueDisabled: !goalPick },
    program: { continueLabel: 'Continue →', onContinue: continueProgram, continueDisabled: !programId },
    component: { continueLabel: busy ? 'Working…' : 'Continue →', onContinue: () => ((componentId || wholeProgram) ? advanceFromScope() : setErr('Pick a component — or the whole program.')), continueDisabled: (!componentId && !wholeProgram) || busy },
    product: { continueLabel: busy ? 'Working…' : 'Continue →', onContinue: continueProduct, continueDisabled: !formatId || busy },
    context: {
      continueLabel: busy ? 'Saving…' : (projectParam && project ? 'Save & continue →' : 'Continue →'),
      onContinue: continueContext, continueDisabled: busy,
      note: undefined,
    },
    assets: { continueLabel: !clientAssets.length ? 'Skip — no assets yet →'
                : copyUnanswered.length ? 'Skip the copy questions →' : 'Continue →',
              continueQuiet: copyUnanswered.length > 0,
              onContinue: () => setStep(stepAfter('assets')), continueDisabled: busy || !!wizAsset },
    count: {
      continueLabel: 'Continue →',
      onContinue: () => {
        if (pickMode === 'chosen' && !chosenIds.length) { setErr('Pick the client’s assets.'); return; }
        setErr(''); setStep('questions');
      },
      continueDisabled: pickMode === 'chosen' && !chosenIds.length,
    },
    questions: {
      continueLabel: busy ? 'Saving…' : (projectParam && project ? 'Save & continue →' : 'Continue →'),
      onContinue: continueQuestions, continueDisabled: busy,
      note: undefined,
    },
    review: isPageBuildLane
      ? { continueLabel: busy ? 'Assembling…' : 'Assemble the pages', onContinue: assembleToPages,
          continueDisabled: busy || !assemblyPossible,
          note: assemblyPossible ? 'Builds each page from the client\u2019s copy, verbatim — then Page build.' : undefined }
      : assemblyPossible
      ? { continueLabel: busy ? 'Working…' : 'Assemble the strategy document from the client\u2019s copy', onContinue: () => startBuild(true),
          note: 'Or go straight to pages / run research — buttons in the summary.' }
      : research
      // Revisit with research already done: rebuild the strategy on the
      // existing research/buyer map; re-running research stays one click away.
      ? { continueLabel: 'Review buyer map & rebuild →', onContinue: () => setStep('research-review'),
          note: 'Opens the buyer map — confirm the output there, then build.' }
      : { continueLabel: 'Run research', onContinue: () => startBuild(false) },
    'research-review': { continueLabel: 'Build the strategy', onContinue: startStrategyWithSpec },
  };

  const stepsIdx = steps.indexOf(step);
  const actionCfg = FOOTERS[step];
  const canGoBack = stepsIdx > 0 || step === 'research-review';

  // ── Render ─────────────────────────────────────────────────────────────

  const chips = [];
  if (program) {
    chips.push({
      label: `${clientName(program.client_id) ? clientName(program.client_id) + ' · ' : ''}${program.program_name}`,
      // Context, not a deliverable — neutral (Edward, 2026-10-22).
      fg: INK.body, bg: NEUTRAL.chipBg,
    });
  }
  if (goalDef) chips.push({ label: goalDef.lane, fg: INK.body, bg: NEUTRAL.chipBg });
  if (fmt) {
    chips.push({
      label: canChangeProduct ? `${fmt.format_name} ✎` : fmt.format_name,
      fg: INK.body, bg: NEUTRAL.chipBg,
      ...(canChangeProduct ? {
        onClick: openProductChange,
        title: 'Change the DemandArc product this plan builds (e.g. the program says DA but the client wants DB)',
      } : {}),
    });
  }

  // Creation: topics progress strictly in rail order (plain index compare).
  // Project mode: done-ness comes from the DATA — setup is answered, research
  // ran, the map has rows, the strategy exists, stage 4 is marked done.
  const topicOrder = RAIL.map(([k]) => k);
  const currentTopicIdx = topicOrder.indexOf(STEP_TOPIC[step]);
  const stage4 = (project?.stages || []).find(s => s.stage_number === 4);
  const topicDone = {
    scope: !!project, product: !!project,
    // Gathering steps tick from their data, not from the project existing
    // (usability pass, 2026-10-22: every step read ✓ with nothing added).
    assets: !!project && clientAssets.length > 0,
    questions: !!project && !!privacyUrl.trim(),
    context: !!project && !!(research || researchSources.length || focusDraft.trim() || notesDraft.trim()),
    review: !!project,
    research: !!research, personas: mapRows.length > 0,
    strategy: !!(strategyFlow || strategyItems.length),
    // Pages with the build team means the copy was definitionally final —
    // without this, a skip-path project read "Final copy: todo" while its
    // package was already built and accepted (Edward, 2026-10-09).
    copy: !!stage4?.done || ['client_approved', 'da_approved'].includes(stage4?.approval_status)
      || (deployInfo?.builds || []).length > 0,
    script: ['client_approved', 'da_approved'].includes(
      (project?.stages || []).find(st => st.stage_number === 5)?.approval_status),
    deploy: !!deployInfo?.rollup?.all_accepted,
    launch: !!deployInfo?.milestones?.launched,
  };
  const railState = (topic) => {
    const i = topicOrder.indexOf(topic);
    if (i === currentTopicIdx) return 'current';
    if (projectParam && project) return topicDone[topic] ? 'done' : 'todo';
    return i < currentTopicIdx ? 'done' : 'todo';
  };
  // The rail IS the navigation once the project exists; during creation it
  // stays a progress display. A running build is never abandoned by a click.
  const railNavigable = !!(projectParam && project) && !['research-run', 'strategy-run'].includes(step);
  // The skip path: nothing for the reasoning chain to decide — the client
  // chose the assets and supplied the copy (deployment gate ready, or pages
  // already in build) and no strategy stands. The chain's not-done rail rows
  // read "skipped" instead of looking forgotten; a step that DID run (say,
  // research out of curiosity) keeps its ✓.
  const skipPath = railNavigable
    && !(strategyFlow || strategyItems.length)
    && !!(deployInfo?.gate?.ready || (deployInfo?.builds || []).length > 0);
  // Landing page build never researches or writes — those rows read
  // "skipped" from the start rather than looking forgotten.
  const skipPathTopics = skipPath || isPageBuildLane ? ['context', 'research', 'personas', 'strategy'] : [];
  const TOPIC_STEP = {
    scope: 'program', product: 'product', context: 'context', assets: 'assets',
    questions: 'questions', review: 'review',
    research: 'research-view', personas: 'buyermap', strategy: 'strategy-view',
    script: 'strategy-view', deploy: 'deploy', launch: 'launch', deck: 'deck',
    // Phase 3/4 sub-steps (display states, not wizard steps) all land on the
    // one panel that owns their phase.
    'deploy-prep': 'deploy', 'deploy-doc': 'deploy', 'deploy-send': 'deploy', 'deploy-back': 'deploy', 'deploy-review': 'deploy',
    'launch-package': 'launch', 'launch-client': 'launch',
    'launch-request': 'launch', 'launch-live': 'launch',
  };
  const leaveWithUnsavedBrand = () => !brandDirty || window.suppressConfirm
    || window.confirm('You have unsaved brand changes. Leave without saving them?');
  // During creation a finished setup screen is one click back, like ← Back
  // (staging pass, 2026-10-23: the ticked rows looked clickable and weren't).
  const creationJump = (topic) => !railNavigable && !!project && railState(topic) === 'done'
    && steps.includes(TOPIC_STEP[topic]) && !['research-run', 'strategy-run'].includes(step);
  const railClick = (topic) => {
    if (!railNavigable && !creationJump(topic)) return;
    if (step === 'deploy' && TOPIC_STEP[topic] !== 'deploy' && !leaveWithUnsavedBrand()) return;
    setErr('');
    setStep(TOPIC_STEP[topic]);
  };

  // ── The four-phase accordion (design settled 2026-10-08) ────────────────
  // Each phase collapses to a one-line outcome; the current phase is open;
  // any phase opens on a header click. Steps are ALWAYS the same for every
  // project — skipped ones grey out, never disappear.
  const currentPhaseKey = (RAIL.find(([k]) => k === STEP_TOPIC[step]) || [])[2] || 'gather';
  const builds = deployInfo?.builds || [];
  const bAccepted = builds.filter(b => b.status === 'accepted').length;
  const bReview = builds.filter(b => ['url_submitted', 'checked'].includes(b.status)).length;
  const ms = deployInfo?.milestones || {};
  const stage3 = (project?.stages || []).find(s => s.stage_number === 3);
  const emailHandoff = !SHOW_BUILD_TEAM_WORKFLOW && builds.length === 0;
  const docRail = buildDocRail({
    stamps: deployInfo?.build_doc, fmtDate,
    ready: !!deployInfo?.build_doc?.ready || ['client_approved', 'da_approved'].includes(stage3?.approval_status),
  });
  const phaseSummary = {
    gather: !project ? 'in progress'
      : !topicDone.assets ? 'no client assets yet'
      : !topicDone.questions ? 'privacy link still needed'
      : 'done — answers saved',
    content: (() => {
      const bits = [];
      if (strategyFlow || strategyItems.length) {
        if (stage3?.approval_status === 'pending_client') bits.push('strategy awaiting client approval');
        else if (['client_approved', 'da_approved'].includes(stage3?.approval_status)) bits.push('strategy approved ✓');
        else bits.push('strategy drafted');
      } else if (skipPath) {
        bits.push("client's own copy · strategy skipped");
      }
      if (topicDone.copy) bits.push('copy ready');
      return bits.join(' · ') || (project ? 'in progress' : 'after gathering');
    })(),
    build: (() => {
      if (emailHandoff) return docRail.summary;
      if (!builds.length) {
        return deployInfo?.gate?.ready
          ? 'ready — send the copy from the Page build screen'
          : 'starts once the page copy is ready';
      }
      if (deployInfo?.rollup?.all_accepted) return `package complete — ${builds.length} of ${builds.length} accepted`;
      const back = bAccepted + bReview;
      return `package: ${back} of ${builds.length} back`
        + (bReview ? ` · ${bReview} for your review` : '');
    })(),
    launchg: (() => {
      if (ms.launched) return `🚀 launched ${ms.launched.at ? fmtDate(ms.launched.at) : ''}`;
      if (ms.launch_requested) return 'with the build team to launch';
      if (ms.client_approved) return 'client approved — ready to launch';
      if (ms.packet_sent) return 'package with the client';
      if (deployInfo?.rollup?.all_accepted) return 'ready — package complete';
      return emailHandoff ? 'after the pages are built' : 'when the package is complete';
    })(),
  };
  const phaseDone = {
    gather: !!project && topicDone.assets && topicDone.questions,
    content: !!topicDone.copy || !!deployInfo?.gate?.ready
      || ['client_approved', 'da_approved'].includes(stage3?.approval_status),
    build: !!deployInfo?.rollup?.all_accepted || (emailHandoff && docRail.done),
    launchg: !!ms.launched,
  };
  const phaseState = (key) => (key === currentPhaseKey ? 'current' : phaseDone[key] ? 'done' : 'todo');
  const phaseOpen = (key) => (phaseOverrides[key] !== undefined ? phaseOverrides[key] : key === currentPhaseKey);
  // Once the project is past Content (pages are with the build team), any
  // content step that never ran reads "skipped" — a plain hollow dot there
  // looks forgotten (Edward, 2026-10-09: "isn't telequal skipped?").
  const contentBehind = builds.length > 0;
  const stepSkipped = (topic) => !topicDone[topic] && (
    skipPathTopics.includes(topic)
    || (contentBehind && ['research', 'personas', 'strategy'].includes(topic))
  );
  // Phases 3 and 4 expand into their real journey (Edward, 2026-10-09:
  // "aren't there sub-steps in Page build and Approve & launch? I can't
  // expand those"). Each sub-step is a display state over live data; clicking
  // any of them opens the one panel that owns the whole phase.
  const allAcceptedRail = !!deployInfo?.rollup?.all_accepted && builds.length > 0;
  const backCount = bAccepted + bReview;
  const buildSubSteps = emailHandoff ? docRail.steps : [
    { key: 'deploy-prep', label: 'Page copy, brand & images',
      done: builds.length > 0 || !!deployInfo?.gate?.ready },
    { key: 'deploy-send', label: 'Send to the build team', done: builds.length > 0 },
    { key: 'deploy-back', label: 'Build team builds the pages',
      done: builds.length > 0 && backCount === builds.length,
      badge: builds.length > 0 && backCount < builds.length
        ? { label: `${backCount} of ${builds.length} back`, tone: 'muted' } : undefined },
    { key: 'deploy-review', label: 'Your review — accept each page', done: allAcceptedRail,
      badge: bReview > 0 ? { label: `${bReview} to review`, tone: 'gate' } : undefined },
  ];
  // Client approval is a real step that can be implicitly skipped: once
  // launch is requested (or live) without it, it greys out as "skipped"
  // rather than looking forgotten — same rule as the content steps.
  const clientSkipped = !ms.client_approved && !!(ms.launch_requested || ms.launched);
  const launchSubSteps = [
    { key: 'launch-package', label: emailHandoff ? 'Pages built' : 'Package complete', done: allAcceptedRail },
    { key: 'launch-client', label: 'Client approval of the package',
      done: !!ms.client_approved, dimmed: clientSkipped,
      badge: clientSkipped ? { label: 'skipped', tone: 'muted' }
        : (ms.packet_sent && !ms.client_approved
          ? { label: 'with the client', tone: 'client' } : undefined) },
    { key: 'launch-request', label: 'Request launch',
      done: !!ms.launch_requested || !!ms.launched },
    { key: 'launch-live', label: 'Launched', done: !!ms.launched,
      badge: ms.launch_requested && !ms.launched
        ? { label: 'with the build team', tone: 'builder' } : undefined },
  ];
  const lateSteps = (list, phaseKey) => {
    const activeIdx = list.findIndex(s => !s.done);
    return list.map((s, i) => ({
      key: s.key, label: s.label,
      state: s.done ? 'done'
        : (currentPhaseKey === phaseKey && i === activeIdx ? 'current' : 'todo'),
      clickable: railNavigable,
      dimmed: s.dimmed,
      badge: s.badge,
    }));
  };
  const railPhases = PHASES.map(([key, number, label]) => {
    const phaseSteps = RAIL.filter(([, , g]) => g === key);
    const late = key === 'build' ? lateSteps(buildSubSteps, 'build')
      : key === 'launchg' ? [...lateSteps(launchSubSteps, step === 'deck' ? 'deck' : 'launchg'),
        deckRailRow(deckRail.step, step === 'deck', railNavigable)] : null;
    return {
      key, number, label,
      state: phaseState(key),
      summary: phaseSummary[key],
      open: phaseOpen(key),
      onHeaderClick: () => setPhaseOverrides(o => ({ ...o, [key]: !phaseOpen(key) })),
      steps: late || phaseSteps.map(([topic, label2]) => ({
        key: topic,
        label: label2,
        state: railState(topic),
        clickable: railNavigable || creationJump(topic),
        dimmed: stepSkipped(topic),
        badge: stepSkipped(topic) ? { label: 'skipped', tone: 'muted' } : undefined,
      })),
    };
  });

  const stepCard = { maxWidth: '680px', display: 'flex', flexDirection: 'column', gap: '10px' };
  const stepTitle = (t) => <div style={{ ...displayFont('22px', 700), letterSpacing: '-0.2px' }}>{t}</div>;
  const stepHint = (t) => <div style={{ fontSize: '13.5px', color: INK.secondary, lineHeight: 1.55 }}>{t}</div>;
  const pickRow = { display: 'flex', flexWrap: 'wrap', gap: '8px' };

  return (
    <WizardChrome
      title={project?.name || (revisit ? 'Content Project' : 'New Content Project')}
      backLabel="← ContentIQ Home"
      onBack={() => { if (leaveWithUnsavedBrand()) navigate('/contentiq'); }}
      chips={chips}
      onAdvanced={projectParam ? () => { if (leaveWithUnsavedBrand()) navigate('/contentiq/advanced'); } : undefined}
      advancedLabel="All projects">

      {copyImportOpen && project && (
        <CopyImportPanel projectId={project.id}
          onClose={() => setCopyImportOpen(false)}
          onApplied={copyImportApplied} />
      )}

      {/* Duplicate-name guard (Edward, 2026-10-08): a project about to be
          created under a name that already exists stops here first — two
          identically-named live projects made his staging test URLs
          unattributable. The suggested variant is prefilled; keeping the
          duplicate stays possible, but as a deliberate click. */}
      {nameClash && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(19, 24, 67, 0.35)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ ...card, maxWidth: '560px', width: '100%', padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: '12px', fontFamily: FONTS.body }}>
            <div style={{ ...displayFont('18px', 700) }}>A project with this name already exists</div>
            <div style={{ fontSize: '13px', color: INK.secondary, lineHeight: 1.6 }}>
              {nameClash.existing.length === 1 ? 'There is already a project named' : `There are already ${nameClash.existing.length} projects named`}{' '}
              <b style={{ color: INK.primary }}>{nameClash.autoName}</b>
              {nameClash.existing[0]?.created_at ? ` (created ${fmtDate(nameClash.existing[0].created_at)}` : ' ('}
              {nameClash.existing[0]?.stage?.label ? ` · ${nameClash.existing[0].stage.label})` : ')'}
              . Two projects with one name are easy to mix up — give this one its own.
            </div>
            <label style={{ fontSize: '12px', color: INK.muted }}>
              Name for the new project
              <input value={nameDraft} onChange={e => setNameDraft(e.target.value)} autoFocus
                style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '4px', padding: '9px 11px', fontSize: '13.5px', fontFamily: FONTS.body, border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small }} />
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={confirmNamedCreate} disabled={busy || !nameDraft.trim()} style={buttonPrimarySmall()}>
                Create the project
              </button>
              <button onClick={() => { setNameClash(null); setErr(''); }} style={buttonSmall}>Cancel</button>
              <span onClick={() => { if (!busy) { setNameDraft(nameClash.autoName); } }}
                style={{ fontSize: '11.5px', color: INK.muted, cursor: 'pointer', textDecoration: 'underline' }}>
                keep the duplicate name anyway
              </span>
            </div>
            {err && <div style={{ fontSize: '12px', color: STATE.danger.text }}>{err}</div>}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>

        {/* The rail — the one map of the project, as the four-phase
            accordion: a progress display during creation, THE navigation
            once the project exists. Phases behind you collapse to their
            outcome; skipped steps grey out but never disappear. */}
        <div style={{ width: '236px', flexShrink: 0, borderRight: `1px solid ${NEUTRAL.borderSoft}`, padding: '18px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <CiqPhaseRail phases={railPhases} onStepClick={railClick} />
          <div style={{ marginTop: 'auto', fontSize: '11px', color: INK.muted, lineHeight: 1.5 }}>
            {railNavigable
              ? 'Open any phase — earlier answers stay saved, and edits feed the next build.'
              : 'Answers are saved as you go — Back never loses them.'}
          </div>
        </div>

        {/* The current step */}
        <div style={{ flexGrow: 1, overflowY: 'auto', padding: '30px 52px 34px', fontFamily: FONTS.body }}>

          {/* The syndication nudge: every asset already carries client-approved
              copy and nothing is in the build yet — the PM can hand the page set
              straight to the build team. A quiet note on the Build plan only,
              never a second primary next to a step's own (consistency pass,
              2026-10-22). */}
          {railNavigable && step === 'review' && deployInfo?.gate?.ready
            && (deployInfo?.builds || []).length === 0 && (
            <div style={{
              maxWidth: '680px', marginBottom: '18px', padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
              background: HUES.strategy.tint, border: `1px solid ${HUES.strategy.border}`,
              borderRadius: RADII.small, fontSize: '12.5px', color: INK.body,
            }}>
              <span style={{ flexGrow: 1 }}>
                All assets have client-approved copy — the page set is ready to send to the build team.
              </span>
              <button onClick={() => setStep('deploy')} style={buttonLink}>Continue to Page build →</button>
            </div>
          )}

          {productChanged && step !== 'loading' && (
            <div role="status" style={{
              maxWidth: '680px', marginBottom: '14px', padding: '10px 14px', display: 'flex', alignItems: 'center',
              gap: '12px', flexWrap: 'wrap', background: STATE.gate.bg, border: `1px solid ${STATE.gate.border}`,
              borderRadius: RADII.small, fontSize: '12.5px', color: STATE.gate.deep,
            }}>
              <span style={{ flex: '1 1 300px' }}>
                The product sold for this project changed in NeTrA — it is now{' '}
                <b>{project.derived_format_name}</b>, but this project still follows{' '}
                <b>{project.product_format_name || 'no product'}</b>.
                {(strategyFlow || strategyItems.length > 0) ? ' Rebuild the strategy after switching.' : ''}
              </span>
              <button onClick={switchToDerived} disabled={busy} style={buttonPrimarySmall()}>
                Switch to {project.derived_format_name}
              </button>
            </div>
          )}

          {laneDeadEnd && (
            <div role="status" style={{
              maxWidth: '680px', marginBottom: '14px', padding: '10px 14px', display: 'flex', alignItems: 'center',
              gap: '12px', flexWrap: 'wrap', background: STATE.gate.bg, border: `1px solid ${STATE.gate.border}`,
              borderRadius: RADII.small, fontSize: '12.5px', color: STATE.gate.deep,
            }}>
              <span style={{ flex: '1 1 300px' }}>
                Landing page build is for Content Syndication products. {laneFormat?.format_name || 'This product'}{' '}
                {laneFormat?.has_branching ? 'branches into several pages' : 'has a page DemandArc writes'}, so its copy
                comes out of the content strategy — Landing page copy builds the strategy and writes every page from it.
              </span>
              <button onClick={() => switchLane('lp')} style={buttonPrimarySmall()}>Switch to Landing page copy</button>
            </div>
          )}

          {step === 'loading' && (
            <div style={stepCard}>
              {stepTitle('Loading your project…')}
              {stepHint('Restoring every setup answer from what’s saved — nothing to re-enter.')}
            </div>
          )}

          {step === 'goal' && (
            <div style={stepCard}>
              {stepTitle('What do you need to produce?')}
              {stepHint('Your near-term objective — it sets where the wizard takes you and what counts as done. Nothing is locked: a strategy project can take on landing pages (and the rest) later from its own rail.')}
              <div style={pickRow}>
                {Object.entries(OUTPUT_DEFS).map(([k, d]) => (
                  <Pick key={k} label={d.title} selected={goalPick === k} onClick={() => setGoalPick(k)} />
                ))}
                <Pick label="Full engagement — all of the above" selected={goalPick === 'full'} onClick={() => setGoalPick('full')} />
              </div>
              {goalDef && (
                <div style={{ fontSize: '12.5px', color: INK.secondary, lineHeight: 1.55 }}>
                  {goalDef.blurb}
                </div>
              )}
              {goalPick === 'full' && (
                <div style={{ fontSize: '12.5px', color: INK.secondary, lineHeight: 1.55 }}>
                  The whole arc — research, buyer map, strategy, pages, launch — with no single
                  output pinned as the deliverable.
                </div>
              )}
              <div style={{ fontSize: '11.5px', color: INK.muted }}>
                The sales enablement deck isn&rsquo;t a project of its own — it&rsquo;s the Sales deck step on every
                Deal Accelerator, Decision Builder+ and Demand Cascade project, once the pages are built.
              </div>
            </div>
          )}

          {step === 'program' && (
            <div style={stepCard}>
              {stepTitle('Which NeTrA program is this for?')}
              {stepHint('The client and scope ride along with the program.')}
              <div style={{ maxWidth: '430px' }}>
                {(() => {
                  const split = splitByStatusGroup(programs, {
                    statusKey: 'status', groupMap: buildStatusGroupMap(programStatuses), dateKey: 'flight_date_end',
                  });
                  const toOpt = pr => ({
                    value: String(pr.id),
                    label: `${clientName(pr.client_id) ? clientName(pr.client_id) + ' — ' : ''}${pr.program_name || `Program #${pr.id}`}`,
                  });
                  return (
                    <SearchableSelect
                      options={split.active.map(toOpt)}
                      closedOptions={split.closed.map(toOpt)}
                      value={programId ? String(programId) : ''}
                      onChange={pickProgram}
                      placeholder="— pick the program —"
                    />
                  );
                })()}
              </div>
              {programComponents.length === 1 && (
                <div style={{ fontSize: '12.5px', color: STATE.done.text }}>
                  ✓ One component — {programComponents[0].component_name} — resolved automatically.
                </div>
              )}
              {/* A component-less program can't derive a product — say so
                  instead of showing a silently blank product screen (F1,
                  Edward's 2026-08-25 Indeed rerun: "you don't seem to be
                  able to see that it's a deal accelerator" — the program
                  had no components at all). */}
              {programId && programComponents.length === 0 && (
                <div style={{ background: STATE.gate.tint, border: `1px solid ${STATE.gate.main}`, borderRadius: RADII.small, padding: '10px 14px', fontSize: '12.5px', color: INK.body, lineHeight: 1.5 }}>
                  ⚠ This program has <b>no components</b>, so there&rsquo;s no product to derive from.
                  You can continue program-wide and pick the DemandArc product yourself on the next
                  screen — or add a component (with its product) to the program in NeTrA first, and
                  the wizard will detect it.
                </div>
              )}
            </div>
          )}

          {step === 'component' && (
            <div style={stepCard}>
              {stepTitle('Which components does this project cover?')}
              {stepHint('One strategy can span the whole program — several components\u2019 pages feeding shared later stages (a Demand Cascade\u2019s shape) — or cover a single component on its own.')}
              <div style={pickRow}>
                <Pick selected={wholeProgram}
                  label={`\ud83c\udf10 This whole program — all ${programComponents.length} components`}
                  onClick={() => { setWholeProgram(true); setComponentId(null); }} />
              </div>
              <div style={{ fontSize: '11.5px', color: INK.muted }}>…or one component:</div>
              <div style={pickRow}>
                {programComponents.map(c => (
                  <Pick key={c.component_id} selected={!wholeProgram && componentId === c.component_id}
                    label={`${c.component_name || `Component #${c.component_id}`}${c.product_name ? ` (${c.product_name})` : ' (no product assigned)'}`}
                    onClick={() => { setWholeProgram(false); setComponentId(c.component_id); }} />
                ))}
              </div>
            </div>
          )}

          {step === 'product' && (
            <div style={stepCard}>
              {stepTitle('What did DemandArc sell?')}
              {stepHint(derivedFormat
                ? `The ${component ? component.component_name + " component's" : "scope's"} product derives ${derivedFormat.format_name} — confirm, or change it. The product shapes the whole content plan.`
                : programId && programComponents.length === 0
                ? 'The program has no components, so nothing derives the product — pick it. It determines what the strategy must produce.'
                : component && !component.product_name
                ? `The ${component.component_name} component has no product assigned in NeTrA, so nothing derives it — pick it here (or set the component's product in NeTrA).`
                : "The scope's product doesn't resolve to one format — pick it. It determines what the strategy must produce.")}
              <div style={pickRow}>
                {formats.map(f => (
                  <Pick key={f.id} selected={formatId === f.id} disabled={busy}
                    label={`${f.format_name}${derivedFormat && derivedFormat.id === f.id ? ' ✓ derived' : ''}`}
                    onClick={() => setFormatId(f.id)} />
                ))}
              </div>
            </div>
          )}

          {step === 'context' && project && (
            <div style={stepCard}>
              {stepTitle('Tell me about the company and campaign')}
              {stepHint('Web pages to research, client materials that inform research but are never promoted (persona overviews, sales sheets), and your own notes.')}
              {researchSources.length > 0 && (
                <div style={{ fontSize: '12.5px', color: INK.body }}>
                  {researchSources.map(sr => <div key={sr.id}>· {sr.title}</div>)}
                </div>
              )}
              <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                <textarea value={urlsDraft} onChange={e => setUrlsDraft(e.target.value)} rows={2}
                  placeholder={'https:// — one or more URLs, one per line'}
                  style={{ ...inputStyle, flex: 1, resize: 'vertical' }} />
                <button onClick={addUrls} disabled={busy} style={buttonSmall}>+ Add</button>
              </div>
              <FileDropZone inputRef={materialsRef} accept={FILE_ACCEPT} disabled={busy}
                label={busy ? 'Uploading…' : 'Drop client materials here — or click to browse'}
                hint="pdf, docx, xlsx, pptx, txt, md, csv, png, jpg — 25MB each"
                onFiles={fs => uploadFiles(fs, {})} />
              <UploadProgressList rows={uploadProg} onCancel={cancelUpload} />
              <button onClick={() => setPasteOpen(o => !o)} style={{ ...buttonSmall, alignSelf: 'flex-start' }}>
                {pasteOpen ? 'Cancel paste' : 'Paste client material instead'}
              </button>
              {pasteOpen && (
                <div style={{ display: 'grid', gap: '6px' }}>
                  <input value={pasteTitle} onChange={e => setPasteTitle(e.target.value)}
                    placeholder="What is this? (e.g. Client's persona map)" style={inputStyle} />
                  <textarea value={pasteText} onChange={e => setPasteText(e.target.value)} rows={4}
                    placeholder="Paste the client material here" style={{ ...inputStyle, resize: 'vertical' }} />
                  <button onClick={addPaste} style={{ ...buttonSmall, justifySelf: 'start' }}>Add pasted material</button>
                </div>
              )}
              <input value={focusDraft} onChange={e => setFocusDraft(e.target.value)}
                placeholder="Campaign focus (optional) — a vertical or audience slice, e.g. AEC firms" style={inputStyle} />
              <textarea value={notesDraft} onChange={e => setNotesDraft(e.target.value)} rows={3}
                placeholder="Your notes — the company, the product being promoted, campaign goals, target titles"
                style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          )}

          {step === 'assets' && project && (
            <div style={stepCard}>
              {stepTitle('The client’s assets')}
              {stepHint(isPageBuildLane
                ? 'Each asset becomes one landing page, built from the client\u2019s own copy, verbatim. Add the assets, then their copy — or import the whole copy document at once.'
                : 'The content this campaign promotes — whitepapers, ebooks, webinars. For each, say whether the client wrote its landing-page copy.')}
              {promotableAll.length > 0 && (
                <div style={{ display: 'grid', gap: '3px', fontSize: '13px', color: INK.body }}>
                  {promotableAll.map(a => {
                    // The row being asked about right now is unmistakable
                    // (Edward, 2026-09-23: "hard to understand which asset
                    // you're on and that you're stepping through the list").
                    const current = wizAsset && wizAsset.id === a.id;
                    const queued = wizQueue.some(x => x.id === a.id);
                    const isOffer = a.flow_role === 'stage2_offer';
                    return (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '2px 6px',
                      borderRadius: RADII.small,
                      background: current ? HUES.matrix.tint : 'none',
                      border: current ? `1px solid ${HUES.matrix.main}` : '1px solid transparent' }}>
                      <span style={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontWeight: current ? 700 : 400 }}>
                        {current ? '▶' : '·'} {a.title}
                      </span>
                      {current && (
                        <span style={{ background: HUES.matrix.main, color: NEUTRAL.pageBg, borderRadius: '10px', padding: '0 8px', fontSize: '10.5px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          answering now
                        </span>
                      )}
                      {!current && queued && (
                        <span style={{ color: INK.muted, fontSize: '10.5px', whiteSpace: 'nowrap' }}>copy question queued</span>
                      )}
                      {!current && !queued && !isOffer && (a.has_client_copy
                        ? <button onClick={() => { setWizAsset(a); setWizCopy(a.client_copy_html || textToHtml(a.client_copy)); }}
                            title="View, edit or replace this asset's client copy"
                            style={{ border: 'none', background: STATE.done.tint, color: STATE.done.text, borderRadius: '10px', padding: '0 8px', fontSize: '10.5px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            ✓ client copy — edit
                          </button>
                        : <button onClick={() => { setWizAsset(a); setWizCopy(''); }}
                            style={{ border: `1px dashed ${NEUTRAL.control}`, background: 'none', color: INK.secondary, borderRadius: '10px', padding: '0 8px', fontSize: '10.5px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            ＋ client copy
                          </button>)}
                      {declaresStages && !current && (
                        <select value={a.flow_role || ''} onChange={e => setAssetFlowRole(a, e.target.value)}
                          title="What stage of the program is this asset for?"
                          style={{ fontSize: '10.5px', color: isOffer ? STATE.gate.text : INK.secondary,
                            border: `1px solid ${isOffer ? STATE.gate.text : NEUTRAL.control}`, borderRadius: '10px',
                            padding: '0 4px', background: isOffer ? STATE.gate.tint : 'none', cursor: 'pointer' }}>
                          <option value="">Stage 1 — landing pages</option>
                          <option value="stage2_offer">Offered on the follow-up call (not a page)</option>
                        </select>
                      )}
                      <button onClick={() => removeWizAsset(a)} title="Remove this asset from the project"
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: INK.muted, fontSize: '12px' }}>✕</button>
                    </div>
                    );
                  })}
                </div>
              )}
              {!wizAsset ? (
                <>
                  <FileDropZone inputRef={assetFileRef} accept={FILE_ACCEPT} disabled={busy}
                    label={busy ? 'Uploading…' : 'Drop the client’s assets here — or click to browse'}
                    hint="25 MB each — add as many as you need, over as many drops as you like; the copy questions wait until you're done"
                    onFiles={onAssetFiles} />
                  <UploadProgressList rows={uploadProg} onCancel={cancelUpload} />
                  {copyUnanswered.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                      padding: '10px 12px', background: HUES.matrix.tint, border: `1px solid ${HUES.matrix.border}`, borderRadius: RADII.small }}>
                      <span style={{ fontSize: '12.5px', color: INK.body, flex: '1 1 260px' }}>
                        Done adding assets? Say whether the client wrote each one's landing-page copy.
                      </span>
                      <button onClick={startCopyQuestions} disabled={busy} style={buttonPrimarySmall(HUES.matrix.main)}>
                        Answer the copy question — {copyUnanswered.length} asset{copyUnanswered.length === 1 ? '' : 's'} →
                      </button>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input value={wizUrl} onChange={e => setWizUrl(e.target.value)}
                      placeholder="or https:// a hosted asset / webinar / video" style={{ ...inputStyle, width: '280px' }} />
                    {wizUrl.trim() && (
                      <input value={wizTitle} onChange={e => setWizTitle(e.target.value)}
                        placeholder="Asset title" style={{ ...inputStyle, width: '200px' }} />
                    )}
                    {wizUrl.trim() && (
                      <button onClick={addUrlAsset} disabled={busy} style={buttonSmall}>Add URL asset</button>
                    )}
                  </div>
                  {/* The one-doc copy import (slice 1, 2026-10-09) — clients
                      often send one spreadsheet/doc holding all the copy and
                      asset links; the review screen maps blocks onto assets. */}
                  <button onClick={() => setCopyImportOpen(true)} disabled={busy}
                    style={{ ...buttonSmall, alignSelf: 'flex-start' }}>
                    Import copy &amp; links from a spreadsheet or doc
                  </button>
                </>
              ) : (
                <div style={{ border: `2px solid ${HUES.matrix.main}`, borderRadius: RADII.tile, background: HUES.matrix.tint, padding: '14px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ background: HUES.matrix.main, color: NEUTRAL.pageBg, borderRadius: RADII.pill, padding: '2px 10px', fontSize: '11px', fontWeight: 700 }}>{isPageBuildLane ? 'the client’s copy' : 'their copy, or ours?'}</span>
                    <span style={{ fontSize: '11.5px', color: INK.secondary }}>
                      asset {clientAssets.length - wizQueue.length > 0 ? clientAssets.length - wizQueue.length : 1} of {clientAssets.length}
                      {wizQueue.length > 0 ? ` — ${wizQueue.length} more after this one` : ' — last one'}
                    </span>
                  </div>
                  <div style={{ fontSize: '14px', color: INK.primary, fontWeight: 700, marginBottom: '2px' }}>
                    ▶ {wizAsset.title}
                  </div>
                  <div style={{ fontSize: '13px', color: INK.body, marginBottom: '6px' }}>
                    {isPageBuildLane
                      ? 'Paste the client’s landing-page copy for this asset — every page in Landing page build uses theirs.'
                      : 'Did the client write the landing-page copy for this asset?'}
                  </div>
                  <RichCopyEditor value={wizCopy} onChange={setWizCopy}
                    placeholder="Paste the client's copy for this asset — formatting survives" />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button onClick={() => saveAssetCopy(wizCopy)} disabled={!htmlToText(wizCopy).trim()}
                      style={htmlToText(wizCopy).trim() ? buttonPrimarySmall(HUES.lp.main)
                        : { ...buttonPrimarySmall(), background: NEUTRAL.controlSoft, color: INK.muted, cursor: 'default' }}>
                      ✓ Use their copy
                    </button>
                    {/* Landing page build writes nothing, so "we'll write it" isn't
                        an answer there — the copy can only come later. */}
                    {isPageBuildLane ? (!wizAsset.has_client_copy && (
                      <button onClick={() => saveAssetCopy('')} style={buttonSmall}>Add it later</button>
                    )) : (
                      <button onClick={() => saveAssetCopy('')} style={buttonSmall}>
                        {wizAsset.has_client_copy ? 'Remove copy — we’ll write it' : 'We\'ll write it'}
                      </button>
                    )}
                    {wizAsset.has_client_copy && (
                      <button onClick={() => { setWizAsset(wizQueue[0] || null); setWizQueue(q => q.slice(1)); setWizCopy(''); }}
                        style={buttonSmall}>Cancel</button>
                    )}
                  </div>
                  {wizAsset.has_client_copy && strategyItems.length > 0 && (
                    <div style={{ fontSize: '11.5px', color: INK.secondary, marginTop: '6px' }}>
                      This project already has a generated strategy — regenerate it (and recreate the
                      page copy) after changing this, or the documents keep the old copy.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 'count' && (
            <div style={stepCard}>
              {stepTitle('Did the client already choose the assets?')}
              {stepHint(`You've added ${clientAssets.length} asset${clientAssets.length === 1 ? '' : 's'}. This is a fact about what the client decided — how many pages, and for which buyers, gets asked after the research, over the actual buyer map.`)}
              {offerAssets.length > 0 && (
                <div style={{ fontSize: '12px', color: STATE.gate.text, background: STATE.gate.tint, borderRadius: RADII.small, padding: '6px 10px' }}>
                  📞 Offered on the follow-up call: <b>{offerAssets.map(a => a.title).join(' · ')}</b> — offered
                  by name on the qualification call, never built as a page, so it isn't choosable here.
                </div>
              )}
              <div style={pickRow}>
                <Pick label="No — recommend for me" selected={pickMode === 'recommend'} onClick={() => setPickMode('recommend')} />
                <Pick label="Yes — the client chose" selected={pickMode === 'chosen'} onClick={() => setPickMode('chosen')} />
              </div>
              {pickMode === 'chosen' && (
                <div>
                  <div style={{ fontSize: '12.5px', color: INK.secondary, marginBottom: '4px' }}>Pick the assets the client chose:</div>
                  {clientAssets.map(a => (
                    <label key={a.id} style={{ display: 'block', fontSize: '13px', margin: '2px 0', cursor: 'pointer' }}>
                      <input type="checkbox" checked={chosenIds.includes(a.id)}
                        onChange={() => setChosenIds(ids => ids.includes(a.id) ? ids.filter(x => x !== a.id) : [...ids, a.id])} /> {a.title}
                    </label>
                  ))}
                  {isPlainDA && chosenIds.length > 1 && (
                    <div style={{ marginTop: '10px', border: `1px solid ${NEUTRAL.border}`, borderRadius: RADII.small, padding: '10px 12px' }}>
                      <div style={{ fontSize: '12.5px', fontWeight: 700, marginBottom: '4px' }}>
                        A {fmt.format_name} builds around one featured asset per build — what are
                        we building with these {chosenIds.length}?
                      </div>
                      <label style={{ display: 'block', fontSize: '12.5px', margin: '2px 0', cursor: 'pointer' }}>
                        <input type="radio" name="ciq-buildplan" checked={buildPlan === 'lanes'}
                          onChange={() => setBuildPlan('lanes')} />{' '}
                        <b>{chosenIds.length} parallel builds</b> — one per asset, as lanes in ONE
                        strategy document (recommended)
                      </label>
                      <label style={{ display: 'block', fontSize: '12.5px', margin: '2px 0', cursor: 'pointer' }}>
                        <input type="radio" name="ciq-buildplan" checked={buildPlan === 'single'}
                          onChange={() => setBuildPlan('single')} />{' '}
                        <b>One build</b> — you'll pick the featured asset at the output step
                      </label>
                      {buildPlan === 'lanes' && (
                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${NEUTRAL.border}` }}>
                          <div style={{ fontSize: '12.5px', fontWeight: 700, marginBottom: '4px' }}>
                            BANT questions &amp; call script across the lanes:
                          </div>
                          <label style={{ display: 'block', fontSize: '12.5px', margin: '2px 0', cursor: 'pointer' }}>
                            <input type="radio" name="ciq-lanescustom" checked={lanesCustom === 'per_lane'}
                              onChange={() => setLanesCustom('per_lane')} />{' '}
                            <b>Customized per lane</b> — each lane gets its own question set and
                            script (common for a multi-lane {fmt.format_name})
                          </label>
                          <label style={{ display: 'block', fontSize: '12.5px', margin: '2px 0', cursor: 'pointer' }}>
                            <input type="radio" name="ciq-lanescustom" checked={lanesCustom === 'generic'}
                              onChange={() => setLanesCustom('generic')} />{' '}
                            <b>Generic</b> — one shared set and one script; only the asset title swaps
                          </label>
                        </div>
                      )}
                      <div style={{ fontSize: '11.5px', color: INK.muted, marginTop: '4px' }}>
                        Recorded now, so the research review and the strategy step already know
                        what's being built.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 'questions' && (
            <div style={stepCard}>
              {stepTitle(bantOptional ? 'Qualification questions' : 'Did the client supply qualification questions?')}
              {bantOptional ? (
                <>
                  {stepHint(`${fmt?.format_name || 'This product'} carries no qualification questions by default. Add them if this campaign needs them — the client's own, or written by DemandArc.`)}
                  <div role="radiogroup" aria-label="Qualification questions" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {[
                      ['none', 'None', 'The registration form asks only for the contact fields.'],
                      ['client', 'The client\u2019s own', 'Paste them exactly as the client gave them.'],
                      ['demandarc', 'DemandArc writes them', qualAsText
                        ? 'Starts from DemandArc\u2019s recommended set — tailor the wording (Need especially). Marked proposed for the client to confirm.'
                        : 'Budget, authority, need and timing, written with the strategy and marked proposed for the client to confirm.'],
                    ].map(([key, label, hint]) => (
                      <label key={key} style={{ display: 'flex', gap: '8px', alignItems: 'baseline', fontSize: '13px', cursor: 'pointer' }}>
                        <input type="radio" name="ciq-qual-source" checked={qualSource === key} onChange={() => pickQualSource(key)} />
                        <span><b style={{ fontWeight: 600 }}>{label}</b>
                          <span style={{ color: INK.muted, fontSize: '12px' }}> — {hint}</span></span>
                      </label>
                    ))}
                  </div>
                  {bantOpen && qualSource !== 'none' && (
                    <RichCopyEditor value={bantText} onChange={setBantText}
                      placeholder={qualSource === 'client' ? 'Paste them exactly as given — formatting and links survive' : 'Tailor the recommended questions'} />
                  )}
                </>
              ) : (
              <>
              {stepHint("Carried verbatim into the build. Add the client's own questions here, or insert DemandArc's recommended set below as a starting point.")}
              <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={bantOpen} onChange={() => setBantOpen(o => !o)} />{' '}
                The client supplied qualification questions (budget, authority, need, timing)
              </label>
              {bantOpen && (
                <RichCopyEditor value={bantText} onChange={setBantText}
                  placeholder="Paste them exactly as given — formatting and links survive" />
              )}
              <div style={{ fontSize: '12.5px' }}>
                <button onClick={() => setRecOpen(o => !o)}
                  style={buttonLink}>
                  {recOpen ? '▾' : '▸'} Insert DemandArc's recommended questions…
                </button>
                {recOpen && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '6px', paddingLeft: '14px' }}>
                    <div style={{ color: INK.muted, fontSize: '11.5px', lineHeight: 1.5 }}>
                      Pick which apply to this campaign — they land in the questions box above as
                      editable text, so tailor the wording before continuing.
                    </div>
                    {RECOMMENDED_QUESTIONS.map(([key, label]) => (
                      <label key={key} style={{ fontSize: '12.5px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!recSet[key]}
                          onChange={() => setRecSet(s => ({ ...s, [key]: !s[key] }))} /> {label}
                      </label>
                    ))}
                    <button onClick={insertRecommended}
                      disabled={!RECOMMENDED_QUESTIONS.some(([k]) => recSet[k])}
                      style={{ ...buttonSmall, alignSelf: 'flex-start' }}>Insert selected</button>
                  </div>
                )}
              </div>
              </>
              )}
              <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={customOpen} onChange={() => setCustomOpen(o => !o)} />{' '}
                The client requires other custom questions
              </label>
              {customOpen && (
                <RichCopyEditor value={customText} onChange={setCustomText}
                  placeholder="Paste them exactly as given — formatting and links survive" />
              )}
              <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={optinOpen} onChange={() => setOptinOpen(o => !o)} />{' '}
                The client supplied opt-in / privacy language for the pages
              </label>
              {optinOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <RichCopyEditor value={optinText} onChange={setOptinText} minHeight="80px"
                    placeholder="Paste the exact opt-in / consent copy — link the privacy policy right in the text (🔗)" />
                  <div style={{ fontSize: '11.5px', color: INK.muted, lineHeight: 1.5 }}>
                    Goes to the build team on every landing-page spec, for all products —
                    the build places this on the form exactly as written, links included.
                  </div>
                </div>
              )}
              {/* Collected on its own, not only as a link inside opt-in copy
                  (Edward, 2026-10-09): with no client opt-in language, the
                  pages still must link the client's privacy policy. */}
              <label style={{ fontSize: '12px', color: INK.muted }}>
                Client privacy policy URL
                <input value={privacyUrl} onChange={e => setPrivacyUrl(e.target.value)}
                  placeholder="https://client.com/privacy"
                  style={{ ...inputStyle, marginTop: '4px',
                    border: privacyUrl.trim() ? inputStyle.border : `1.5px solid ${STATE.gate.strong}` }} />
              </label>
              <div style={{ fontSize: '11.5px', color: privacyUrl.trim() ? INK.muted : STATE.gate.text, lineHeight: 1.5, marginTop: '-4px' }}>
                Needed before the pages go to the build team — every registration form's opt-in
                links the client's own privacy policy. Add it now or later; Page build asks for it again.
              </div>
            </div>
          )}

          {step === 'review' && (
            <div style={stepCard}>
              {stepTitle("Content plan — here's what I'll do")}
              <div style={{ background: BRAND.navy, borderRadius: RADII.card, padding: '22px 26px', color: NEUTRAL.pageBg, boxShadow: SHADOW.focus }}>
                <div style={{ display: 'grid', gap: '9px', fontSize: '14px', lineHeight: 1.5, opacity: 0.94 }}>
                  {isPageBuildLane && !assemblyPossible ? (
                    hasOursPage ? (
                      <div>{fmt?.format_name || 'This product'} has a landing page DemandArc writes from the
                        strategy, so it can&rsquo;t be built from your own copy yet. Switch this project to
                        Landing page copy — importing a finished strategy document is planned after launch.</div>
                    ) : (
                      <div>Every page needs the client&rsquo;s copy before it goes to the build team.
                        {pageBuildMissingCopy.length
                          ? ` Still missing on: ${pageBuildMissingCopy.map(a => a.title).join(', ')}.`
                          : ' Add the client\u2019s assets first.'}
                        {' '}Add it on Client assets — one asset at a time, or import the whole copy document.</div>
                    )
                  ) : assemblyPossible && isPageBuildLane ? (
                    <div>Every page has the client&rsquo;s copy — {chosenAssets.length} page{chosenAssets.length === 1 ? '' : 's'}.
                      Each goes to the build team exactly as written, with {planQuestions()}.</div>
                  ) : assemblyPossible ? (
                    <div>The client has decided everything — {chosenAssets.length} chosen asset{chosenAssets.length === 1 ? '' : 's'} with
                      their copy. Nothing to research; the pages carry {planQuestions()}.
                      Assemble the strategy document, or go straight to the landing pages.</div>
                  ) : (
                    <>
                      <div>1 · Research {clientName(program?.client_id) || 'the company'} from your {researchSources.length} source{researchSources.length === 1 ? '' : 's'} and materials.</div>
                      <div>2 · Map the <b>whole buying committee</b> — every role, every funnel stage,
                        one bucket of pain points per role per stage — and show you the full buyer
                        map before anything else builds.</div>
                      <div>3 · Then ask what the output needs to be — how many pages, aimed at which
                        of those buyers, at which stage — and {pickMode === 'chosen' && chosenIds.length
                          ? `build around the ${chosenIds.length} asset${chosenIds.length === 1 ? '' : 's'} the client chose.`
                          : clientAssets.length
                          ? `recommend the strongest of your ${clientAssets.length} asset${clientAssets.length === 1 ? '' : 's'} for exactly that.`
                          : 'flag the content gaps — no client assets yet.'}</div>
                      <div>4 · Draft the strategy document with proposed copy
                        {clientAssets.some(a => a.has_client_copy) ? ' — the client’s own copy verbatim where supplied — ' : ' '}
                        and {planQuestions()}.</div>
                    </>
                  )}
                </div>
                {(assemblyPossible || research) && !isPageBuildLane && (
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.14)', marginTop: '14px', paddingTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {assemblyPossible && (
                      <button onClick={assembleToPages} disabled={busy}
                        style={{ ...buttonSmall, background: 'transparent', color: NEUTRAL.pageBg, border: '1.5px solid rgba(255,255,255,0.4)' }}>
                        {busy ? 'Assembling…' : 'Straight to landing pages →'}
                      </button>
                    )}
                    {research && (
                      <button onClick={() => setStep('research-view')}
                        style={{ ...buttonSmall, background: 'transparent', color: NEUTRAL.pageBg, border: '1.5px solid rgba(255,255,255,0.4)' }}>
                        View research &amp; buyer map
                      </button>
                    )}
                    <button onClick={() => startBuild(false)}
                      style={{ ...buttonSmall, background: 'transparent', color: NEUTRAL.pageBg, border: '1.5px solid rgba(255,255,255,0.4)' }}>
                      {research ? '↻ Re-run research' : 'Run research anyway'}
                    </button>
                  </div>
                )}
              </div>
              {!isPageBuildLane && !assemblyPossible && (
                <div style={{ fontSize: '12.5px', color: INK.secondary }}>
                  Two short builds, both reviewed: research &amp; the buyer map first, then the strategy.
                  Nothing goes to the client — you adjust everything.
                </div>
              )}
            </div>
          )}

          {step === 'research-run' && (
            <div style={stepCard}>
              {stepTitle('Researching…')}
              <GenerationProgress messages={RESEARCH_PROGRESS_MSGS} />
              <div style={{ fontSize: '12px', color: INK.muted }}>
                Build 1 of 2 — the buyer map comes back for your review before the strategy builds.
              </div>
            </div>
          )}

          {step === 'research-review' && (
            <div style={{ ...stepCard, maxWidth: '760px' }}>
              {stepTitle('The research is in — review the buyer map')}
              {stepHint('These are the key inputs to the strategy. Build on them as they are, or edit rows on the Buyer map step afterwards.')}
              {research?.company_profile && (
                <div style={{ background: NEUTRAL.band, border: `1px solid ${NEUTRAL.border}`, borderRadius: RADII.tile, padding: '12px 16px', fontSize: '13px', color: INK.body, whiteSpace: 'pre-wrap' }}>
                  {String(research.company_profile).slice(0, 700)}{String(research.company_profile).length > 700 ? '…' : ''}
                </div>
              )}
              <ResearchSources research={research} sources={sources} />
              {(research?.personas || []).length > 0 && (
                <div>
                  <div style={{ fontSize: '12.5px', fontWeight: 700, color: INK.body, marginBottom: '4px' }}>Personas</div>
                  {research.personas.map((pe, i) => (
                    <div key={i} style={{ borderLeft: `3px solid ${HUES.matrix.main}`, paddingLeft: '8px', margin: '4px 0', fontSize: '13px' }}>
                      <b>{pe.name}</b> <span style={{ color: INK.secondary }}>({pe.role_category})</span>
                      <div style={{ color: INK.body }}>{(pe.pain_points || []).join(' · ')}</div>
                    </div>
                  ))}
                </div>
              )}
              {mapRows.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 700, color: INK.body, marginBottom: '4px' }}>
                    Buyer map — built automatically ({mapRows.length} row{mapRows.length === 1 ? '' : 's'})
                  </div>
                  <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12.5px' }}>
                    <thead><tr>
                      {['Persona', 'Stage', 'Pain point', 'Approach'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '4px 8px', borderBottom: `2px solid ${NEUTRAL.border}`, fontSize: '11.5px', color: INK.secondary }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {mapRows.map(r => (
                        <tr key={r.id}>
                          <td style={{ padding: '4px 8px', borderBottom: `1px solid ${NEUTRAL.borderSoft}`, fontWeight: 600 }}>{r.persona_name || '—'}</td>
                          <td style={{ padding: '4px 8px', borderBottom: `1px solid ${NEUTRAL.borderSoft}`, whiteSpace: 'nowrap' }}>{r.funnel_stage}</td>
                          <td style={{ padding: '4px 8px', borderBottom: `1px solid ${NEUTRAL.borderSoft}` }}>{r.pain_point}</td>
                          <td style={{ padding: '4px 8px', borderBottom: `1px solid ${NEUTRAL.borderSoft}`, color: INK.body }}>{r.recommended_approach}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* The output decision, asked OVER the researched map (Edward,
                  2026-08-26): what does the output need to be? Selects from
                  the full matrix — never shrinks it. */}
              {(() => {
                return (
                  <div style={{ border: `2px solid ${BRAND.button}`, borderRadius: RADII.tile, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                    <div style={{ ...displayFont('16px', 700) }}>What does the output need to be?</div>
                    {isBranching && pickMode === 'recommend' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '12.5px', fontWeight: 700, color: INK.body }}>Branches:</span>
                        {[2, 3, 4].map(n => (
                          <Pick key={n} label={String(n)} selected={branchCount === n} onClick={() => setBranchCount(n)} />
                        ))}
                        <span style={{ fontSize: '11.5px', color: INK.muted }}>
                          the routing question gets one answer per branch — each maps to its own asset and page-2 copy
                        </span>
                      </div>
                    )}
                    {isBranching && pickMode === 'chosen' && (
                      <div style={{ fontSize: '12.5px', color: INK.secondary }}>
                        The client chose {chosenIds.length} asset{chosenIds.length === 1 ? '' : 's'} — {chosenIds.length} branch{chosenIds.length === 1 ? '' : 'es'}, one per asset.
                      </div>
                    )}
                    {isPlainDA && (
                      <div style={{ fontSize: '12.5px', color: INK.secondary }}>
                        Deal Accelerator — one asset, one path; the product sets the pages (the final page is the asset itself).
                      </div>
                    )}
                    {lanesPlanned && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12.5px', fontWeight: 700, color: INK.body }}>
                          Building {chosenIds.length} lanes — one per asset, in one strategy document.
                        </span>
                        <span style={{ fontSize: '11.5px', color: INK.body }}>
                          BANT &amp; script: {lanesCustom === 'per_lane'
                            ? 'customized per lane' : 'generic (asset title swaps)'}.{' '}
                          <span onClick={() => setLanesCustom(c => (c === 'per_lane' ? 'generic' : 'per_lane'))}
                            style={{ color: BRAND.link, cursor: 'pointer', fontWeight: 600 }}>
                            Switch to {lanesCustom === 'per_lane' ? 'generic' : 'per lane'}
                          </span>
                        </span>
                        <span style={{ fontSize: '11.5px', color: INK.muted }}>
                          Decided at the asset step.{' '}
                          <span onClick={() => setBuildPlan('single')}
                            style={{ color: BRAND.link, cursor: 'pointer', fontWeight: 600 }}>
                            Switch to a single build
                          </span>
                        </span>
                      </div>
                    )}
                    {needsFeaturedPick && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                        <span style={{ fontSize: '12.5px', fontWeight: 700, color: INK.body }}>
                          The client chose {chosenAssets.length} assets — which one does this build feature?
                        </span>
                        <div style={pickRow}>
                          {chosenAssets.map(a => (
                            <Pick key={a.id} label={a.title || a.filename || `Asset ${a.id}`}
                              selected={featuredId === a.id} onClick={() => setFeaturedId(a.id)} />
                          ))}
                        </div>
                        <span style={{ fontSize: '11.5px', color: INK.muted }}>
                          Single build — one featured asset.{' '}
                          <span onClick={() => setBuildPlan('lanes')}
                            style={{ color: BRAND.link, cursor: 'pointer', fontWeight: 600 }}>
                            Switch to {chosenIds.length} lanes (one build per asset)
                          </span>
                        </span>
                      </div>
                    )}
                    {isTwoTouch && pickMode === 'recommend' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '12.5px', fontWeight: 700, color: INK.body }}>Asset pairs:</span>
                          {Array.from({ length: pairChoices }, (_, i) => i + 1).map(n => (
                            <Pick key={n} label={String(n)} selected={pairsPick === n} onClick={() => setPairsPick(n)} />
                          ))}
                        </div>
                        <span style={{ fontSize: '11.5px', color: INK.muted }}>
                          Each pair is one landing page for the Engagement Phase 1 asset and one
                          follow-up email for its Engagement Phase 2 asset.
                        </span>
                      </div>
                    )}
                    {isTwoTouch && pickMode === 'chosen' && (
                      <div style={{ fontSize: '12.5px', color: INK.secondary }}>
                        The client chose {chosenIds.length} asset{chosenIds.length === 1 ? '' : 's'} — {chosenPairs}{' '}
                        pair{chosenPairs === 1 ? '' : 's'}, one asset per engagement phase.
                        {chosenIds.length % 2 === 1 && chosenIds.length > 1
                          ? ' One asset is left over; the strategy pairs the strongest.' : ''}
                      </div>
                    )}
                    {!isTwoTouch && !isBranching && !isPlainDA && pickMode === 'recommend' && clientAssets.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '12.5px', fontWeight: 700, color: INK.body }}>Pages:</span>
                        {Array.from({ length: Math.min(clientAssets.length, 6) }, (_, i) => i + 1).map(n => (
                          <Pick key={n} label={String(n)} selected={pagesPick === n} onClick={() => setPagesPick(n)} />
                        ))}
                        {fmt?.asset_count ? (
                          <span style={{ fontSize: '11.5px', color: INK.muted }}>
                            {fmt.format_name} typically builds around {fmt.asset_count}
                          </span>
                        ) : null}
                      </div>
                    )}
                    {pickMode === 'chosen' && !needsFeaturedPick && !isBranching && !isTwoTouch && (
                      <div style={{ fontSize: '12.5px', color: INK.secondary }}>
                        The client chose {chosenIds.length} asset{chosenIds.length === 1 ? '' : 's'} — the pages build around them.
                      </div>
                    )}
                    <div style={{ fontSize: '11.5px', color: INK.muted }}>
                      The strategy draws on the whole researched map — pages for the full
                      audience, every funnel stage.
                    </div>
                  </div>
                );
              })()}
              <div style={{ fontSize: '12px', color: INK.muted }}>
                Want to edit rows or re-run with different inputs? Back returns to the setup;
                rows can also be edited on the Buyer map step after the build.
              </div>
            </div>
          )}

          {step === 'strategy-run' && (
            <div style={stepCard}>
              {stepTitle('Building the strategy…')}
              <GenerationProgress messages={STRATEGY_PROGRESS_MSGS} />
              <div style={{ fontSize: '12px', color: INK.muted }}>
                Build 2 of 2 — you land at the review checkpoint when it's done.
              </div>
            </div>
          )}

          {/* ── Build/Deliver sections (project mode; rail-navigated) ── */}

          {step === 'research-view' && (
            <div style={{ ...stepCard, maxWidth: '760px' }}>
              {stepTitle('Research')}
              {research ? (
                <>
                  {research.company_profile && (
                    <div style={{ background: NEUTRAL.band, border: `1px solid ${NEUTRAL.border}`, borderRadius: RADII.tile, padding: '12px 16px', fontSize: '13px', color: INK.body, whiteSpace: 'pre-wrap' }}>
                      {String(research.company_profile)}
                    </div>
                  )}
                  <ResearchSources research={research} sources={sources} />
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={() => startBuild(false)} style={buttonSmall}>↻ Re-run research</button>
                    <div style={{ flexGrow: 1 }} />
                    <button onClick={() => setStep('buyermap')} style={buttonPrimary()}>Continue to Buyer map →</button>
                  </div>
                </>
              ) : (
                <>
                  {stepHint('Research hasn’t run yet — the Content plan kicks it off.')}
                  <button onClick={() => setStep('review')} style={{ ...buttonSmall, alignSelf: 'flex-start' }}>
                    Continue to the Content plan →
                  </button>
                </>
              )}
            </div>
          )}

          {step === 'buyermap' && project && (
            <div style={{ maxWidth: '940px' }}>
              {research && (
                <div style={{ marginBottom: '10px' }}>
                  <ResearchSources research={research} sources={sources} collapsible />
                </div>
              )}
              <Stage2Panel project={project} personas={personas} mapRows={mapRows}
                onReload={loadMapRows} onStageChanged={patchStage} />
              {(strategyFlow || strategyItems.length > 0) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '4px 0 14px' }}>
                  <button onClick={() => setStep('strategy-view')} style={buttonPrimary()}>
                    Continue to Content strategy →
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 'strategy-view' && project && (
            <div style={{ maxWidth: '940px' }}>
              <Stage3Panel project={project} strategy={strategyItems} flow={strategyFlow}
                meta={strategyMeta} onReload={loadStrategyData}
                // The rail's "sent to the build team" line and the Page build gate
                // read deployInfo, and an approval change clears the sent stamp.
                onStageChanged={(next) => { patchStage(next); loadDeployInfo(); }}
                buildsCount={(deployInfo?.builds || []).length}
                strategyOnly={project?.entry_mode === 'specific_output' && effectiveGoal === 'strategy'}
                onContinueToCopy={() => { setErr(''); setStep('deploy'); }}
                onOpenBuyerMap={() => setStep('buyermap')} />
            </div>
          )}

          {step === 'deploy' && project && (
            <div style={{ maxWidth: '940px' }}>
              {/* The strategy's own progress, only while it is the thing being
                  waited on: never on a project that skipped the strategy, and
                  not after the send — the package's progress takes over. */}
              {(strategyFlow || strategyItems.length > 0) && (deployInfo?.builds || []).length === 0 && (
                <StrategyJourney projectId={project.id} compact
                  stage={(project.stages || []).find(st => st.stage_number === 3)}
                  meta={strategyMeta} generatedAt={strategyFlow?.generated_at}
                  buildsCount={0} />
              )}
              <DeploymentPanel projectId={project.id} deploy={deployInfo} onReload={loadDeployInfo}
                approvedDocName={strategyMeta?.brief?.approved_doc?.filename}
                onOpenLaunch={() => setStep('launch')}
                onOpenQuestions={() => { if (leaveWithUnsavedBrand()) setStep('questions'); }}
                onSavePrivacy={async (url) => {
                  await saveContext({ client_privacy_policy_url: url });
                  setPrivacyUrl(url);
                  await loadDeployInfo();
                }}
                onSaveFieldPlacement={async (placement) => {
                  await saveContext({ form_field_placement: placement });
                  await loadDeployInfo();
                }}
                copySection={(
                  <Stage4Panel project={project} strategy={strategyItems}
                    meta={strategyMeta} onStageChanged={patchStage} section="copy" embedded
                    copyFromAssets={isPageBuildLane}
                    autoCreate={['client_approved', 'da_approved'].includes(stage3?.approval_status)}
                    onCopyChanged={loadDeployInfo} />
                )}
                brandSection={(
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {program?.client_id && (
                      <ClientBrandCard clientId={program.client_id}
                        clientName={clientName(program.client_id)} onDirtyChange={setBrandDirty}
                        onSaved={loadDeployInfo} />
                    )}
                    <div style={{ fontSize: '12px', color: INK.secondary }}>
                      This project's own brand files — campaign-specific material, used only here.
                    </div>
                    {brandFiles.length > 0 && (
                      <div style={{ fontSize: '12.5px', color: INK.body }}>
                        {brandFiles.map(b => <div key={b.id}>· {b.title}</div>)}
                      </div>
                    )}
                    <FileDropZone inputRef={brandFileRef} accept=".pdf,.png,.jpg,.jpeg,.gif,.txt,.md,.docx,.pptx" disabled={busy}
                      label={busy ? 'Uploading…' : 'Drop project brand files here — or click to browse'}
                      hint="png, jpg, gif, pdf and docs"
                      onFiles={async fs => { await uploadFiles(fs, { kind: 'brand' }); loadDeployInfo(); }} />
                    <UploadProgressList rows={uploadProg} onCancel={cancelUpload} />
                  </div>
                )} />
            </div>
          )}

          {step === 'deck' && project && (
            <div style={{ maxWidth: '940px' }}>
              <SalesDeckPanel projectId={project.id} onChanged={deckRail.refresh} />
            </div>
          )}

          {step === 'launch' && project && (
            <div style={{ maxWidth: '940px' }}>
              <ApproveLaunchPanel projectId={project.id} deploy={deployInfo} onReload={loadDeployInfo}
                onOpenBuild={() => setStep('deploy')} />
            </div>
          )}

          {err && <div style={{ color: STATE.danger.text, fontSize: '12.5px', marginTop: '12px', maxWidth: '680px' }}>{err}</div>}

          {/* The step's actions sit WITH its content (Edward, 2026-08-25:
              the far-away, below-the-fold footer button). */}
          {actionCfg && (
            <div style={{ maxWidth: step === 'research-review' ? '760px' : '680px', display: 'flex', alignItems: 'center', gap: '12px', marginTop: '20px', paddingBottom: '10px' }}>
              {canGoBack && <button onClick={goBack} style={buttonSmall}>← Back</button>}
              <div style={{ flexGrow: 1 }} />
              {(actionCfg.note || savedNote) && (
                <span style={{ fontSize: '12px', color: savedNote ? STATE.done.text : INK.muted }}>
                  {savedNote ? `✓ ${savedNote === 'questions' ? 'Questions' : 'Company & campaign'} saved — edits feed the next build or re-assembly.` : actionCfg.note}
                </span>
              )}
              <button onClick={actionCfg.onContinue} disabled={!!actionCfg.continueDisabled || laneDeadEnd}
                title={laneDeadEnd ? 'Switch to Landing page copy above to continue' : undefined}
                style={{
                  ...(actionCfg.continueQuiet ? buttonSmall : buttonPrimary()),
                  ...(actionCfg.continueDisabled || laneDeadEnd ? buttonDisabled : {}),
                }}>
                {actionCfg.continueLabel}
              </button>
            </div>
          )}
        </div>
      </div>
    </WizardChrome>
  );
}
